/**
 * The single source of user-facing language for Here For.
 *
 * Keep this package free of view and domain logic. A view selects a typed message;
 * it does not compose new copy. See ../CONTRACT.md for the content rules.
 */

export interface TitledMessage {
  readonly title: string;
  readonly body: string;
}

export interface ActionableMessage extends TitledMessage {
  readonly action: string;
}

export {
  COERCIVE_VOCABULARY,
  RULE_CATALOGUE_EXPORTS,
  sweepForCoerciveVocabulary,
} from "./toneGuard.js";
export type { SweepSummary, ToneFinding } from "./toneGuard.js";

export const TONE_RULES = [
  "Use ordinary UK English and active voice.",
  "Name the action and its consequence in short, natural sentences.",
  "Keep the register calm, warm and matter-of-fact.",
  "Preserve consent: could help, has been asked and has agreed are different things.",
  "Never shame, score, moralise or imply that somebody has let the family down.",
  "Do not manufacture urgency or imply that Here For is an emergency service.",
  "Refer to the supported person as a person, never as a case, patient, dependency or resource.",
  "Avoid technical jargon, care-sector bureaucracy and exaggerated reassurance.",
  "Use contractions where they sound natural.",
] as const;

export const BRAND = {
  productName: "Here For",
  yourPlans: "Your plans",
  planTitle: (supportedPersonName: string): string => `Here for ${supportedPersonName}`,
  inPlanLabel: (supportedPersonName: string): string => `${supportedPersonName}’s plan`,
} as const;

export const CANONICAL_VOCABULARY = {
  whatsHappening: "What’s happening",
  helpNeeded: "Help needed",
  couldHelp: "Could help",
  hasBeenAsked: "Has been asked",
  hasAgreed: "Has agreed",
  needsSomeone: "Needs someone",
  covered: "Covered",
  pleaseRecheck: "Please recheck",
  organiser: "Organiser",
  people: "People",
  practicalUpdate: "Practical update",
  removeAccess: "Remove access",
  addToPlan: "Add to plan",
  helpFor: (supportedPersonName: string): string => `Help ${supportedPersonName} needs`,
  /**
   * The Agenda's heading for drafts whose time is not settled (plan §9.5, §14.4).
   *
   * Every other group in that list is a date, so this one has to be a heading too
   * rather than a footnote. "No date yet" says the thing is real and the time is
   * outstanding — which is exactly what an event waiting on a clinic to ring back is.
   */
  noDateYet: "No date yet",
} as const;

export const DISCOURAGED_VOCABULARY = [
  { avoid: "care-management portal", use: "plan" },
  { avoid: "schedule artefacts", use: "what’s happening" },
  { avoid: "resource requirement", use: "help needed" },
  { avoid: "eligible", use: "could help" },
  { avoid: "self-assign", use: "I can help" },
  { avoid: "assigned", use: "has agreed" },
  { avoid: "unallocated", use: "needs someone" },
  { avoid: "staffed", use: "covered" },
  { avoid: "invalidated assignment", use: "please recheck" },
  { avoid: "administrator", use: "organiser" },
  { avoid: "users", use: "people" },
  { avoid: "case note", use: "practical update" },
] as const;

export const USER_VISIBLE_STATE_KEYS = [
  "needsSomeone",
  "covered",
  "pleaseRecheck",
  "done",
  "cancelled",
] as const;

export type UserVisibleState = (typeof USER_VISIBLE_STATE_KEYS)[number];

export interface UserVisibleStateCopy {
  readonly label: string;
  readonly explanation: string;
}

export const USER_VISIBLE_STATES = {
  needsSomeone: {
    label: "Needs someone",
    explanation: "Nobody has agreed yet.",
  },
  covered: {
    label: "Covered",
    explanation: "The required help has been agreed.",
  },
  pleaseRecheck: {
    label: "Please recheck",
    explanation: "Something important changed.",
  },
  done: {
    label: "Done",
    explanation: "The family confirmed this happened.",
  },
  cancelled: {
    label: "Cancelled",
    explanation: "The event or need will not happen.",
  },
} as const satisfies Record<UserVisibleState, UserVisibleStateCopy>;

/** Secondary context only: neither value is a sixth primary state. */
export const STATUS_CONTEXT = {
  past: "Past",
  askedWaitingForReply: "Asked — waiting for reply",
  askedExplanation:
    "This is still marked ‘Needs someone’ until somebody accepts. It is not covered yet.",
  askedPersonWaiting: (displayName: string): string =>
    `${displayName} has been asked — waiting for reply. This is not covered yet.`,
  coverageCount: (confirmed: number, required: number): string =>
    `${String(confirmed)} of ${String(required)} ${required === 1 ? "person" : "people"} confirmed`,
} as const;

export const ACTION_LABELS = {
  addToPlan: "Add to plan",
  offerToHelp: "I can help",
  commit: "I can do this",
  confirmCommitment: "Yes, I can do this",
  askSomeone: "Ask someone",
  recheck: "Please recheck",
  reconfirm: "Yes, I can still do this",
  declineAfterChange: "No, I can’t now",
  withdraw: "I can’t do this now",
  recordSomeoneElse: "Record someone else",
  reviewLatest: "Review the latest version",
  tryAgain: "Try again",
  saveAvailability: "Save availability",
} as const;

export const NAVIGATION_LABELS = {
  today: "Today",
  /**
   * The helper's own screen (bead hf-e6-commitments-coverage-udt.9).
   *
   * "My week", not "My tasks" or "My jobs": what a person has agreed to is a shape
   * their week has taken, not a workload somebody assigned them. It sits second in
   * the shell rather than last because the adoption bottleneck is the helper, and a
   * screen they have to go looking for is a screen they never open twice.
   */
  myWeek: "My week",
  calendar: "Calendar",
  availability: "Availability",
  people: "People",
  settings: "Settings",
  help: "Help",
} as const;

export const AVAILABILITY_MESSAGES = {
  question: (supportedPersonName: string): string => `When could you help ${supportedPersonName}?`,
  meaning: "This means the family may ask you. It does not book you in.",
  selected: "I could help",
  notSet: "Not set",
  saved: "Saved. Everyone can now see your availability.",
  practicalModesQuestion: "How could you help?",
  fullTimeMatch: "Could help for the full time",
  partialMatch: "Partly overlaps",
  timingTight: "Timing is tight",
  informationMissing: "Not enough information",
} as const;

export const COMMITMENT_MESSAGES = {
  consequence: (responsibility: string): string =>
    `You’ll be marked as responsible for ${responsibility}. The family will see your name.`,
  withdrawn: "You’re no longer marked as doing this. The family can now find someone else.",
  proxyRecorded: (helperName: string, recorderName: string, source: string): string =>
    `${helperName} is helping. Recorded by ${recorderName} after agreement in ${source}.`,
} as const;

/**
 * Optional organiser-private withdrawal reasons (COM-012, §10.10; bead hf-pp4).
 *
 * Every option describes a circumstance, never a standard the helper failed to
 * meet. `other` deliberately stands on its own: V1 asks for no elaboration or
 * free-text justification.
 */
export const WITHDRAWAL_REASON_CODES = [
  "unwell",
  "family_change",
  "schedule_clash",
  "transport",
  "no_longer_available",
  "other",
] as const;

export type WithdrawalReasonCode = (typeof WITHDRAWAL_REASON_CODES)[number];

export const WITHDRAWAL_REASON_LABELS = {
  unwell: "I’m unwell",
  family_change: "Family circumstances changed",
  schedule_clash: "My plans now clash",
  transport: "Transport is no longer available",
  no_longer_available: "I’m no longer available",
  other: "Something else",
} as const satisfies Record<WithdrawalReasonCode, string>;

export const RECONFIRMATION_MESSAGES = {
  title: "Something important changed",
  introduction: "Please check the updated details and make a new choice.",
  impact: (displayNames: string): string =>
    `This change affects ${displayNames}. Their help will be marked “Please recheck” until they confirm the new details.`,
  previousValueLabel: "Was",
  currentValueLabel: "Now",
  confirm: "Yes, I can still do this",
  decline: "No, I can’t now",
} as const;

export const EMPTY_STATE_KEYS = [
  "nothingPlannedToday",
  "noAvailabilityNextWeek",
  "noOneHasAddedAvailability",
  "everythingCovered",
  "startPlan",
] as const;

export type EmptyState = (typeof EMPTY_STATE_KEYS)[number];

export const EMPTY_STATES = {
  nothingPlannedToday: {
    title: "Nothing planned today",
    body: "The family has not added anything for today.",
  },
  noAvailabilityNextWeek: {
    title: "No availability added for next week yet",
    body: "Add times when the family may ask you to help.",
  },
  noOneHasAddedAvailability: {
    title: "No one has added availability yet",
    body: "People can add times when they could help.",
  },
  everythingCovered: {
    title: "Everything is covered",
    body: "No support needs are currently open.",
  },
  startPlan: {
    title: "Start with what’s happening",
    body: "Add a plan or some help that is needed.",
  },
} as const satisfies Record<EmptyState, TitledMessage>;

/**
 * Stable rejection codes fixed by the domain and commitment bead graph. Keeping
 * the catalogue as a Record makes a newly added code a deliberate copy change.
 */
export const ACTION_REJECTION_REASONS = [
  "NEED_JUST_COVERED",
  "PLACES_AWAITING_RECHECK",
  "STALE_REVISION",
  "ALREADY_COMMITTED",
  "NEED_NOT_ACTIVE",
] as const;

export type ActionRejectionReason = (typeof ACTION_REJECTION_REASONS)[number];

export const REJECTION_MESSAGES = {
  NEED_JUST_COVERED: {
    title: "Someone else just stepped in",
    body: "This has already been covered. Thank you for checking.",
    action: "View the latest plan",
  },
  /**
   * Every place is held by somebody who has been asked to confirm they can still do
   * the new arrangement (bead hf-83p).
   *
   * THE SENTENCE THIS AVOIDS is "this is full", which is not true — the plan says
   * Please recheck, not Covered, and a person who read that and offered deserves
   * better than a contradiction. It also avoids naming anybody: who is deciding is on
   * the need for everyone to see, and putting a name in a refusal turns one person's
   * ordinary delay into something the offerer is waiting on them for.
   *
   * No apology, and no instruction to come back later. If the answer is No the need
   * reopens on its own and they will see it.
   */
  PLACES_AWAITING_RECHECK: {
    title: "The people helping are checking they still can",
    body: "The plan changed, so they have been asked again. If anyone can’t, this will need someone.",
    action: "View the latest plan",
  },
  STALE_REVISION: {
    title: "This changed since you looked",
    body: "Here is the latest version. Please check the details before deciding again.",
    action: "Review the latest version",
  },
  ALREADY_COMMITTED: {
    title: "You’re already helping with this",
    body: "Your help is already recorded. There is nothing else you need to do.",
    action: "View details",
  },
  NEED_NOT_ACTIVE: {
    title: "This no longer needs a response",
    body: "The plan has changed. Here is the latest information.",
    action: "View the latest plan",
  },
} as const satisfies Record<ActionRejectionReason, ActionableMessage>;

/**
 * What surrounds a refused claim (bead hf-e6-commitments-coverage-udt.4.2).
 *
 * THE SENTENCE THIS EXISTS TO PREVENT is "you were too slow". Losing the last
 * place is the normal outcome of two people being kind at the same moment, and the
 * person who arrived second did a generous thing. So there is no blame here, no
 * urgency, no runner-up framing, and — deliberately — no apology either: apologising
 * would imply something went wrong, and nothing did. The need is covered, which is
 * the outcome everybody wanted (§10.9, COV-006).
 *
 * `REJECTION_MESSAGES.NEED_JUST_COVERED` carries the headline. These are the two
 * things a person needs next: what the plan says NOW, and somewhere else their offer
 * can go.
 */
export const CLAIM_OUTCOME_MESSAGES = {
  /**
   * The truth about the need, shown beside the refusal.
   *
   * Reading "2 of 2 people confirmed" is what turns a refusal into an explanation.
   * The claim guard returns both numbers with the rejection precisely so this line
   * can be written without waiting for the plan to reload (NED-012, COV-006).
   */
  nowCovered: (confirmed: number, required: number): string =>
    `This is now ${STATUS_CONTEXT.coverageCount(confirmed, required)}.`,
  /** Heading over other needs that are still open. An offer, never a prompt. */
  stillNeededTitle: "Other help still needed",
  /**
   * Shown when nothing else is open.
   *
   * Warm and final: the person came to help, everything is covered, and they should
   * leave knowing that rather than feeling sent away.
   */
  nothingElseNeeded: "Everything else is covered just now. Thank you for offering.",
  /** Confirms their own help landed. Plain, and never congratulatory. */
  accepted: "You’re down to help. The family can see your name.",
} as const;

/**
 * Calendar export (§20.1, §20.2, CAL-001 through CAL-006; bead hf-e8-sharing-export-x1v.1).
 *
 * THE EXPORTED FILE LEAVES THE PRODUCT. It lands in Apple Calendar, in Google
 * Calendar, on a lock screen, in a notification a stranger on a train can read over
 * somebody's shoulder. So the DEFAULT title says nothing about the supported person's
 * name, their condition, or where they are going — "Helping T." and no more (CAL-003,
 * §4.6 Class C). Full detail is an opt-in the member chooses for their own calendar.
 *
 * The staleness sentence is the plan's own words (§20.2), parameterised by the plan
 * title rather than hardcoded to one family.
 */
export const CALENDAR_EXPORT_MESSAGES = {
  /** The privacy-safe default. An initial, never a name (CAL-003). */
  discreetTitle: (supportedPersonInitial: string): string => `Helping ${supportedPersonInitial}.`,
  /** Opt-in, for a member who wants their own calendar to say what the help is. */
  detailedTitle: (helpLabel: string, supportedPersonName: string): string =>
    `${helpLabel} — ${supportedPersonName}`,
  /**
   * §20.2, verbatim except for the plan title.
   *
   * A downloaded file cannot always be updated after import, and the honest thing is
   * to say so in the file itself, where the person reads it — not only on the button
   * they pressed a week ago.
   */
  stalenessWarning: (planTitle: string): string =>
    `This copy may not update automatically. Check ${planTitle} if anything changes.`,
  /** Which record wins when they disagree. */
  authoritative: (planTitle: string): string => `${planTitle} has the current plan.`,
  /** The feed's display name in a calendar app. Carries no supported-person name. */
  feedName: "Here For — your help",
} as const;

/**
 * The calendar link a member manages for themselves (CAL-003, CAL-007; bead hf-7o6).
 *
 * THE WORD IS "LINK", NEVER "TOKEN". What a member holds is a web link they paste
 * into a calendar app. That it is also a 256-bit credential is true, and is exactly
 * why the consequences below are spelled out — but "rotate your bearer token" is a
 * sentence for the people who built this, not for somebody sorting out lifts to a
 * clinic.
 *
 * EVERY SENTENCE THAT CHANGES SOMETHING NAMES THE CONSEQUENCE, NOT THE ACTION. The
 * replace copy is deliberately not "get a fresh link": what a person needs to know
 * before pressing it is that the old link stops working and a calendar still using it
 * quietly stops updating. The cheerful half of that sentence is the half they already
 * understand.
 *
 * WHY THE PRIVACY SENTENCES ARE BLUNT. The CAL-007 ruling accepted two residual risks
 * on the member's behalf (docs/decisions-log.md): the link does not expire, and a
 * calendar company may keep its own copy of what it fetches. A member choosing to show
 * more detail is the person who carries that risk, so they are told about it where
 * they choose — not in a privacy page they will never open.
 */
export const CALENDAR_FEED_MESSAGES = {
  heading: "Your calendar link",
  explanation:
    "Add the times you’ve agreed to help to your own calendar. It updates on its own, so you don’t have to come back here to check.",

  /** Nobody has made a link yet. Offered, never nagged. */
  none: {
    title: "You haven’t set up a calendar link",
    body: "You don’t need one. It’s here if having these times in your own calendar would help.",
    action: "Create a calendar link",
  },

  /** What the member's own feed is doing, for a state chip. */
  liveLabel: "On",
  offLabel: "Off",

  createdOn: (formattedDate: string): string => `Set up on ${formattedDate}.`,

  /**
   * How recently somebody fetched this link.
   *
   * COARSE ON PURPOSE, and the reason is not only technical. This is the one signal a
   * member gets that a link they have forgotten about is still being used, so it has
   * to be readable at a glance rather than precise to the minute.
   */
  lastFetched: {
    never: "Nothing has used this link yet.",
    withinHour: "A calendar used this link in the last hour.",
    hoursAgo: (hours: number): string =>
      `A calendar used this link about ${String(hours)} ${hours === 1 ? "hour" : "hours"} ago.`,
    daysAgo: (days: number): string =>
      `A calendar used this link about ${String(days)} ${days === 1 ? "day" : "days"} ago.`,
    unexpected:
      "If you weren’t expecting that, replace the link. The old one stops working straight away.",
  },

  /**
   * The one time the link is ever shown.
   *
   * Said plainly rather than reassuringly: a member who skims this and closes the
   * panel has lost the link, and no amount of soft phrasing gets it back.
   */
  reveal: {
    title: "Here’s your calendar link",
    body: "This is the only time it’s shown. Paste it into your calendar app now — if you lose it, you can make a new one.",
    action: "I’ve added it to my calendar",
  },
  linkLabel: "Calendar link",
  whoCanSee:
    "Anyone with this link can see when you’ve agreed to help, without signing in. Keep it to yourself, and replace it if you think somebody else has it.",

  /** Rotation. The consequence leads, because the consequence is the decision. */
  replace: {
    title: "Replace this link",
    body: "The link you have now stops working, and any calendar using it stops updating until you add the new one.",
    action: "Replace the link",
  },
  replaceConfirm: {
    title: "Replace your calendar link?",
    body: "Your current link stops working straight away. You’ll see the new one once, and you’ll need to add it to your calendar again.",
    action: "Replace it",
    /** The way out. Named for what it keeps, so the safe choice is the plain one. */
    cancel: "Keep the link I have",
  },
  replaced: {
    title: "Your old link has stopped working",
    body: "Add the new one below to your calendar. Until you do, these times won’t update there.",
  },

  /** Revocation. "Turn off" is what a person is doing; "revoke" is what the row does. */
  turnOff: {
    title: "Turn off this calendar link",
    body: "The link stops working and nothing new reaches your calendar. Times already in it may stay there until you remove them yourself.",
    action: "Turn it off",
  },
  turnOffConfirm: {
    title: "Turn off your calendar link?",
    body: "Nothing new will reach your calendar. You can set up a new link whenever you like.",
    action: "Turn it off",
    cancel: "Leave it on",
  },
  turnedOff: {
    title: "Your calendar link is off",
    body: "Nothing is reaching your calendar from this plan. Times already saved there may stay until you remove them.",
  },

  /**
   * The detail switch (CAL-003).
   *
   * The caching sentence sits with the choice rather than in a policy page, because
   * this is the moment a member decides how much of their life to hand to a calendar
   * company — and it is the only moment they can decide it.
   */
  titleMode: {
    legend: "What your calendar shows",
    discreetLabel: "Just an initial",
    discreetHint: "Your calendar shows “Helping A.” and the time. Nothing else.",
    detailedLabel: "What the help is",
    detailedHint: "Your calendar shows what you’ve agreed to do, and who it’s for.",
    cachingNote:
      "Whatever you choose, your calendar company keeps its own copy of what it fetches. Turning the link off here doesn’t reach that copy.",
  },

  /**
   * A retry that the server recognised as a repeat.
   *
   * The honest and slightly awkward answer: a link exists, and we genuinely cannot
   * show it. Saying so and offering the way out beats inventing a reassurance.
   */
  alreadyCreated: {
    title: "A link was already made",
    body: "That request reached us twice, so we can’t show you the link it made. Replace it to get one you can see.",
    action: "Replace the link",
  },
} as const;

/**
 * Circle settings (CIR-006, CIR-012, CIR-018; bead hf-e3b-circle-people-r3z.2).
 *
 * The one screen where a family configures the product rather than using it, so the
 * register shifts slightly: still calm and plain, but it has to be ACCURATE about
 * consequences in a way most of the product does not, because two of these settings
 * change what everybody else sees.
 *
 * THE TIMEZONE SENTENCE IS THE HARDEST COPY IN THE PRODUCT, and it is hard because
 * the truth is counter-intuitive. Nothing that has been agreed moves. The same
 * physical moments are simply shown against a different clock — so an appointment
 * somebody agreed to at 10:15 will read 11:15 tomorrow, and nobody has been moved.
 * Saying only "this is dangerous" would be useless; saying "nothing will change"
 * would be false. It has to say both halves.
 *
 * BLOCK NAMES ARE NOT INVENTED HERE. Morning, afternoon and evening already exist in
 * CANONICAL_VOCABULARY's world and in the kernel's `TimeBlockId`; this block supplies
 * only the words around them.
 */
export const CIRCLE_SETTINGS_MESSAGES = {
  heading: "Plan settings",
  explanation: "How this plan is set up. Only organisers can change these.",
  /** Shown instead of the controls to somebody without plan.manage. */
  readOnlyNotice: "You can see these settings. An organiser can change them.",
  saved: "Saved.",

  planName: {
    legend: "What this plan is called",
    label: "Plan name",
    hint: "Everyone in the circle sees this, and it appears on printouts.",
    action: "Save the name",
    empty: "Give the plan a name.",
  },

  blocks: {
    legend: "What morning, afternoon and evening mean here",
    /**
     * §11.4: the interface does not explain block boundaries unless somebody asks.
     * This screen IS somebody asking, so it explains.
     */
    hint: "People say “Tuesday afternoon” rather than a time, so the plan needs to know what that means for your family. Changing these changes what everyone picks from, and how the week is laid out.",
    startLabel: "Starts",
    endLabel: "Ends",
    action: "Save these times",
    /** A gap is allowed on purpose: a circle with no evening cover is a real circle. */
    gapsAllowed: "Blocks don’t have to run into each other. Leaving a gap is fine.",
  },

  /** One sentence per validation code the server can return. */
  blockProblems: {
    malformed: "Use 24-hour times, like 08:00 or 17:30.",
    endsBeforeStart: (blockName: string): string => `${blockName} ends before it starts.`,
    overlaps: (earlier: string, later: string): string =>
      `${earlier} runs into ${later}. They can’t share the same time.`,
  },

  timeZone: {
    legend: "Time zone",
    label: "The plan’s time zone",
    hint: "Every time in the plan is shown in this zone.",
    action: "Change the time zone",
    /**
     * Both halves, in the order that matters: nothing moves, and yet everything
     * reads differently. Either half alone is a lie of omission.
     */
    consequence:
      "Nothing that’s already been agreed moves. The same moments are shown against a different clock, so a time that reads 10:15 today may read differently afterwards.",
    confirmTitle: "Change the plan’s time zone?",
    confirmBody:
      "Everyone will see the same arrangements shown at different clock times. Nobody is moved, and nothing is cancelled — but tell the circle, because the times on their screens will change.",
    confirmAction: "Change it",
    cancel: "Leave it as it is",
    unknown: "That isn’t a time zone we recognise. Try one like Europe/London.",
  },

  supportedPerson: {
    legend: "The person this plan is for",
    nameLabel: "Name",
    /**
     * THE D-091 GATE, said out loud in the interface during development.
     *
     * Every supported-person name in this repository is invented, and the founder has
     * not yet confirmed the real spelling (bead hf-e10-hardening-pilot-f4o.5). A
     * screen that presented a placeholder as settled is how a guessed spelling ends up
     * on a printout in somebody's kitchen.
     */
    placeholderNotice:
      "This name is a stand-in and hasn’t been confirmed yet. Check the spelling with the family before this plan is used for real.",
    printContactLabel: "How to reach them, for the printed plan",
    printContactHint:
      "Written in your own words — “landline in the hall”, or whatever is actually useful. It appears on printouts only.",
    action: "Save",
  },
} as const;

/**
 * The family's own copy of their plan (PLT-013, PLT-015, §28.17;
 * bead hf-e10-hardening-pilot-f4o.7).
 *
 * THIS FILE OUTLIVES THE PRODUCT. That is its entire purpose: a circle's
 * coordination history should not be hostage to an app, a company or a database
 * (D-075, §29.18). So the words here are written for somebody reading a text file in
 * five years, possibly after Here For has stopped existing, possibly having never
 * used it — which rules out product jargon, capability names and any sentence that
 * assumes a screen is nearby.
 *
 * Three tone decisions worth stating, because they are easy to undo by accident:
 *
 *  - IT SAYS WHAT IS MISSING. A partial copy that looks complete is worse than no
 *    copy, because somebody will trust it. `omittedSection` / `omittedFields` name
 *    the gap and say who can fill it, without naming a capability.
 *  - IT NEVER CLAIMS A STATUS. The file lists who agreed and how many people were
 *    needed; it does not print "Covered". Coverage is derived from live records
 *    (COV-001, COV-002, invariant 1), and a status frozen into a document a year ago
 *    is a statement that has quietly stopped being true.
 *  - IT DOES NOT OVERSTATE THE DIGEST. `digestMeaning` says damage, not tampering.
 *    The authenticated, encrypted artefact is a different bead
 *    (`hf-e10-hardening-pilot-f4o.9`) with the key custody that would make a stronger
 *    claim honest.
 */
export const PORTABILITY_EXPORT_MESSAGES = {
  documentTitle: (planTitle: string): string => `${planTitle} — your copy`,
  introduction:
    "This is a copy of what the family recorded: what was happening, what help was needed, when people could help, and who agreed to what. It was made on the date below and does not update itself.",
  authoritative: (planTitle: string): string =>
    `${planTitle} has the current plan. Where this copy and the plan disagree, the plan is right.`,
  independence:
    "You can keep this file wherever you like. It stays readable without Here For, and nobody needs an account to open it.",

  madeOnLabel: "Made on",
  timeZoneLabel: "Times are shown in",
  formatLabel: "Format",
  digestLabel: "File digest",
  digestMeaning:
    "Two copies with the same digest hold the same information. It is a check against a file being damaged, not against somebody changing it on purpose.",

  includedTitle: "What’s in this copy",
  includedCountsHeading: "Section",
  includedRecordsHeading: "Records",
  omissionsTitle: "What’s not in this copy",
  omissionsNone: "Nothing was left out.",
  /** Names the gap and who can close it. Deliberately never names a capability. */
  omittedSection: (section: string): string =>
    `${section} — not included. An organiser can make a copy that includes this.`,
  omittedFields: (section: string, fields: string): string =>
    `${section} — ${fields} not included. An organiser can make a copy that includes them.`,

  sections: {
    people: "People",
    /**
     * Access, in the words a reader has (bead hf-dil).
     *
     * Not "Memberships": a family reading a copy of their own plan is not thinking
     * about a table. What the section answers is when each person could see and do
     * things here, which is what the title says.
     */
    memberships: "Who could see and do what",
    events: "What’s happening",
    /**
     * The two histories that make "Please recheck" explicable after the fact.
     *
     * Named by what they answer rather than by the table: a reader who sees somebody
     * was asked to check again wants to know WHAT MOVED, and §28.6 is why the
     * material fields were kept at all.
     */
    eventRevisions: "What changed, and when",
    needs: "Help needed",
    needRevisions: "What changed about the help needed",
    directRequests: "People who were asked",
    commitments: "Who agreed",
    availability: "Times people could help",
    practicalUpdates: "Practical updates",
    contactMethods: "Contact details",
  },
  /** No account handles in a copy of a plan (plan §28.17; bead hf-dil). */
  membershipsMeaning:
    "This is what each person could see and do, and from when. It does not include any login details.",
  revisionsMeaning:
    "Only the changes that could affect what someone had agreed to are listed here.",
  /** Where the versions actually are, so a heading is not a dead end. */
  revisionsLocation: "Each one is listed with the thing it changed.",
  /** "Version 3". A revision is a number to the code and a version to a reader. */
  revisionLabel: (revision: number): string => `Version ${String(revision)}`,
  /**
   * The place changed, without saying to what.
   *
   * Event history keeps a FINGERPRINT rather than the address (EVT-008), precisely so
   * a private location is not copied into history. The summary says what the
   * fingerprint means and no more.
   */
  locationChanged: "the place changed",
  nothingRecorded: "Nothing was recorded here.",

  peopleNeeded: (count: number): string =>
    count === 1 ? "1 person needed" : `${String(count)} people needed`,
  /** Availability is possibility, never consent — the sentence says so where it is read. */
  availabilityMeaning:
    "These are times people said they could be asked. They are not agreements to do anything.",
  askedMeaning:
    "Being asked is not the same as agreeing. Only the agreements below counted towards the help needed.",
  redactedUpdate: "This update was removed.",
  unknownPerson: "Somebody no longer in this copy",

  dictionaryTitle: "What the words mean",
  dictionaryEntitiesTitle: "The records",
  dictionaryVocabulariesTitle: "The words used for states",

  /**
   * The closed vocabularies, in plain words.
   *
   * Every code that appears in the machine-readable half of an export has an entry
   * here, so the two halves of the file explain each other.
   */
  vocabulary: {
    helpModes: {
      in_person: "In person",
      driving: "Driving",
      phone_or_online: "By phone or online",
    },
    needTypes: {
      visit: "A visit",
      drive: "A lift",
      accompany: "Going along with someone",
      call: "A phone call",
      errand: "An errand",
      other: "Something else",
    },
    importance: {
      ordinary: "Ordinary",
      important: "Important",
      critical: "Critical",
    },
    participation: {
      active: "Taking part",
      invited: "Invited",
      offline: "Taking part, not using the app",
      removed: "No longer in the plan",
    },
    eventStates: {
      draft: "Draft",
      scheduled: "Planned",
      completed: "Happened",
      cancelled: "Cancelled",
    },
    needStates: {
      draft: "Draft",
      open: "Open",
      completed: "Finished",
      cancelled: "Cancelled",
    },
    requestStates: {
      pending: "Waiting for a reply",
      accepted: "Accepted",
      declined: "Declined",
      expired: "Expired without a reply",
      cancelled: "Cancelled",
      superseded: "Replaced after the details changed",
    },
    commitmentStates: {
      accepted: "Agreed",
      reconfirmation_required: "Please recheck",
      withdrawn: "Withdrew",
      completed: "Done",
      cancelled: "Cancelled",
    },
    outcomes: {
      done: "Done",
      not_completed: "Did not happen",
      follow_up: "Needs following up",
      unknown: "Not recorded",
    },
    availabilityStates: {
      available: "Could help",
      away: "Away",
      withdrawn: "No longer offered",
    },
    recordSources: {
      self: "Recorded by the person themselves",
      proxy: "Recorded by somebody else on their behalf",
    },
    circleStatuses: {
      active: "Active",
      suspended: "Paused",
      closed: "Closed",
    },
    contactKinds: {
      sms: "Text message",
      email: "Email",
    },
    contactAssurance: {
      clerk_verified: "Verified by the person",
      organiser_confirmed: "Confirmed by an organiser",
      unverified: "Not verified",
    },
  },
} as const;

/**
 * The event surfaces (EVT-*, plan §10.2 and §10.12;
 * bead hf-e4-events-needs-6r5.1).
 *
 * An Event is something happening for the supported person. It is NOT a request for
 * help — that is a Support Need, with its own window — so nothing here says
 * "covered", "needed" or "someone", and `eventNeverCovered` exists to say so out
 * loud on a screen where an organiser might reasonably expect otherwise (EVT-018).
 *
 * THREE THINGS THIS WORDING IS CAREFUL ABOUT.
 *
 * 1. TWO TITLES, AND WHY. `titleLabel` is private and `shareTitleLabel` travels.
 *    Somebody who may not see "Oncology follow-up" may see "Appointment" (EVT-017),
 *    and the hints say which is which at the point of typing — after the fact is too
 *    late, because the private one has already been written down.
 *
 * 2. CANCELLING IS NOT DELETING (invariant 6, §10.12). `cancelTitle` and
 *    `cancelBody` say what survives; `deleteDraftTitle` is the ONLY delete wording
 *    in the product, and it is reachable only for a draft nobody ever saw. The two
 *    read differently on purpose: an organiser must never think they are the same
 *    button in different clothes.
 *
 * 3. A MATERIAL EDIT IS ANNOUNCED BEFORE IT HAPPENS (§10.8: no hidden invalidation).
 *    `materialWarningTitle`/`Body` appear while the organiser is still typing, from
 *    the kernel's own classifier — not afterwards as an apology. The sentence names
 *    the consequence for other people, because that is the fact that might change
 *    the organiser's mind.
 */
export const EVENT_SURFACE_MESSAGES = {
  createTitle: "Add something to the plan",
  editTitle: "Edit this",
  detailTitle: "What's happening",

  titleLabel: "What is it?",
  titleHint: "Only people in the plan can see this.",
  shareTitleLabel: "Short version, for sharing",
  shareTitleHint:
    "This can be seen by anyone with a share link, so keep it general — “Appointment” rather than what it is for.",
  categoryLabel: "What kind of thing is it?",
  startsAtLabel: "Starts",
  endsAtLabel: "Ends",
  timeUnknownHint: "Leave both blank if the time isn't confirmed yet.",
  locationLabel: "Where",
  locationPrivateLabel: "More detail about where",
  locationPrivateHint: "Only people in the plan see this. It is never shared or sent in a message.",
  practicalNoteLabel: "Anything practical to know?",
  practicalNoteHint:
    "Keep it brief and practical. Nothing medical, and nothing the whole plan does not need to see.",

  /**
   * The state of the record, in the organiser's words.
   *
   * Deliberately NOT the five user-visible statuses: those belong to help needed, and
   * an event never has one (EVT-018). These say where the record itself got to.
   */
  states: {
    draft: "Not shared yet",
    scheduled: "In the plan",
    completed: "Happened",
    cancelled: "Cancelled",
  },
  draftExplanation: "Nobody else can see this yet. Publish it when you're ready.",
  eventNeverCovered:
    "Events don't need people. Add the help you need as a separate thing, with its own times.",

  save: "Save",
  saved: "Saved.",
  create: "Add to the plan",
  edit: "Edit",
  publish: "Publish to the plan",
  publishTitle: "Publish this to the plan?",
  publishBody: "Everyone in the plan will be able to see it.",

  cancelEvent: "Cancel this",
  cancelTitle: "Cancel this?",
  cancelBody:
    "It stays in the plan, marked cancelled, so everyone can see what changed. Nothing is deleted.",
  /** The blast radius. Shown only when there is one, and always as a count of people. */
  cancelImpact: (peopleCount: number): string =>
    peopleCount === 1
      ? "1 person has agreed to help with this. They'll be told it's cancelled."
      : `${String(peopleCount)} people have agreed to help with this. They'll be told it's cancelled.`,
  cancelConfirm: "Yes, cancel it",
  keep: "Keep it",

  /**
   * Deleting is only ever offered for a draft (EVT-013, §10.12).
   *
   * Nobody has seen it, nobody agreed to anything, and there is no history to
   * preserve — which is the entire reason this wording is allowed to exist.
   */
  deleteDraft: "Delete this draft",
  deleteDraftTitle: "Delete this draft?",
  deleteDraftBody: "Nobody has seen it, so there's nothing to keep. This can't be undone.",
  deleteDraftConfirm: "Yes, delete it",

  materialWarningTitle: "This changes what people agreed to",
  materialWarningBody:
    "Anyone helping with this will be asked to check the new details and confirm again. Their help won't count until they do.",
  /** After the fact, when the server has told us it really was material. */
  materialSaved: "Saved. The people helping have been asked to check the new details.",
  cosmeticSaved: "Saved. Nobody needs to do anything.",

  historyTitle: "What's changed",
  historyIntroduction:
    "Only changes that could affect what somebody agreed to are kept here. Wording fixes aren't.",
  historyEmpty: "Nothing important has changed since this was added.",
  historyEntry: (revision: number): string => `Version ${String(revision)}`,
  historyTimeChanged: "The time changed.",
  historyLocationChanged: "The place changed.",
  historyCreated: "Added to the plan.",
} as const;

/**
 * The support-need surfaces (NED-*, plan §10.3;
 * bead hf-e4-events-needs-6r5.2).
 *
 * ── THE ONE THING THIS WORDING EXISTS TO GET RIGHT ──────────────────────────────
 *
 * A need's window is NOT its event's window. The driver for a 10:15 appointment is
 * needed from 09:15 to 13:00, and neither end matches the appointment. NED-002 and
 * invariant 2 make that structural; NED-010 forbids showing a helper the event's time
 * as though it were theirs. But the bead names the real risk, which is not a bug —
 * it is that "when the help is needed" and "when the appointment is" are the most
 * confusable pair of ideas in the product for the family members using it.
 *
 * So the wording does three things a label alone would not:
 *
 *  - It asks a QUESTION rather than naming a field. "When do you need somebody?" is
 *    hard to answer with the appointment time by accident; "Starts" is not.
 *  - It says the appointment time OUT LOUD, next to the window, as context —
 *    `aroundEvent` — so the organiser can see the two are different and set them
 *    apart deliberately rather than discovering the distinction later.
 *  - It explains the canonical case in the product's own voice, once, where somebody
 *    filling the form will read it (`windowIndependenceHint`). The example is the
 *    plan's own, because an abstract explanation of this never lands.
 *
 * ── AVL-007 IS A REFUSAL, NOT A REPAIR ──────────────────────────────────────────
 *
 * An overnight window is rejected rather than split in two (plan §11.6). The message
 * for it already exists — `WINDOW_REJECTION_MESSAGES.crosses_day_boundary` — and this
 * block deliberately does not restate it. One rule, one sentence.
 *
 * ── AND IT NEVER SAYS "ASSIGNED" ────────────────────────────────────────────────
 *
 * Capacity is a number of PEOPLE NEEDED, never slots, allocations or staffing.
 * `CANONICAL_VOCABULARY` and `DISCOURAGED_VOCABULARY` both take a position on this;
 * these words follow it.
 */
export const NEED_SURFACE_MESSAGES = {
  createTitle: "What help is needed?",
  editTitle: "Change what's needed",
  listTitle: "Help needed",

  labelLabel: "What needs doing?",
  labelHint: "A few words the family will recognise — “Driver to the clinic and back”.",
  needTypeLabel: "What kind of help is it?",

  /**
   * The window questions.
   *
   * Phrased as questions, and about PEOPLE rather than about the event, because the
   * failure this form is guarding against is somebody typing the appointment time.
   */
  windowTitle: "When do you need somebody?",
  windowStartLabel: "From",
  windowEndLabel: "Until",
  windowIndependenceHint:
    "This is when you need a person, which is usually not the same as when the appointment is. A driver for a 10:15 appointment might be needed from 09:15 until 13:00 — leaving early, waiting, and getting home again.",
  /** Shown beside the window when the need hangs off an event, as context. */
  aroundEvent: (eventWindowLabel: string): string =>
    `The appointment itself is ${eventWindowLabel}.`,
  /** Confirms, in plain words, that the two are deliberately different. */
  windowDiffersFromEvent:
    "You're asking for help over a longer period than the appointment. That's usually right.",
  windowMatchesEvent:
    "This matches the appointment exactly. If somebody needs to arrive earlier or stay later, widen it.",

  capacityLabel: "How many people do you need?",
  capacityHint: "Most things need one person. Ask for two when one genuinely isn't enough.",
  helpModesLabel: "What does somebody need to be able to do?",
  helpModesHint: "Pick everything that would work.",
  importanceLabel: "How much does it matter if nobody can?",
  importanceHint:
    "This only changes how early we remind people. It is not a medical judgement and nobody is ranked by it.",

  meetingDetailLabel: "Where and how to meet",
  meetingDetailHint: "Practical only — where to park, which door, who to ask for.",
  practicalNoteLabel: "Anything else practical?",

  /** The kinds of help a need can ask for, in the family's words. */
  needTypes: {
    visit: "A visit",
    drive: "A lift",
    accompany: "Going along with somebody",
    call: "A phone call",
    errand: "An errand",
    other: "Something else",
  },
  helpModes: {
    in_person: "Be there in person",
    driving: "Drive",
    phone_or_online: "Phone or online",
  },
  importanceLevels: {
    ordinary: "It would help",
    important: "It matters",
    critical: "It really matters",
  },

  /** The need record's own four stored states (plan §28.3), not its coverage. */
  states: {
    draft: "Not shared yet",
    open: "In the plan",
    completed: "Finished",
    cancelled: "Cancelled",
  },
  draftExplanation: "Nobody can offer to help with this yet. Publish it when you're ready.",

  save: "Save",
  create: "Ask for this help",
  edit: "Change this",
  publish: "Ask the family",
  publishTitle: "Ask the family for this help?",
  publishBody: "Everyone in the plan will see it, and anybody who can help will be able to offer.",

  cancelNeed: "No longer needed",
  cancelTitle: "This help is no longer needed?",
  cancelBody:
    "It stays in the plan, marked cancelled, so everyone can see what changed. Nothing is deleted.",
  cancelImpact: (peopleCount: number): string =>
    peopleCount === 1
      ? "1 person has agreed to help. They'll be told it's no longer needed, and thanked."
      : `${String(peopleCount)} people have agreed to help. They'll be told it's no longer needed, and thanked.`,
  cancelConfirm: "Yes, it's not needed",
  keep: "Keep it",

  deleteDraft: "Delete this draft",
  deleteDraftTitle: "Delete this draft?",
  deleteDraftBody: "Nobody has seen it, so there's nothing to keep. This can't be undone.",
  deleteDraftConfirm: "Yes, delete it",

  markDone: "This happened",
  markDoneTitle: "Mark this as done?",
  markDoneBody: "Only mark it done if you know it actually happened.",
  markDoneConfirm: "Yes, it happened",
  /**
   * Completing a need before its window has even started.
   *
   * NED-013 allows it and requires it to leave a trail, so the wording says plainly
   * that this is the unusual case rather than hiding it behind the ordinary button.
   */
  markDoneEarlyWarning:
    "This hasn't happened yet, going by the times. Marking it done now is recorded as an organiser's correction.",

  materialWarningTitle: "This changes what people agreed to",
  materialWarningBody:
    "Anyone helping with this will be asked to check the new details and confirm again. Their help won't count until they do.",

  peopleNeeded: (count: number): string =>
    count === 1 ? "1 person needed" : `${String(count)} people needed`,
  noNeedsYet: "Nothing has been asked for yet.",
  noNeedsYetBody: "When the family needs a hand with something, it goes here.",
} as const;

/**
 * Volunteering, and stepping back (COM-*, §10.10;
 * bead hf-e6-commitments-coverage-udt.2).
 *
 * The words around the product's most consequential tap, and its opposite.
 *
 * ── WITHDRAWING MUST NOT COST ANYTHING ──────────────────────────────────────────
 *
 * §10.10 and COM-013 are explicit: withdrawing does not demand an excuse, and the
 * copy stays neutral. So `withdrawReasonLabel` says the reason is optional in the
 * label itself rather than in fine print underneath, `withdrawNoReasonNeeded` says it
 * again in a sentence, and there is no wording anywhere that implies somebody is
 * letting the family down. A person who can no longer do a hospital run at 7am is
 * having a bad enough day.
 *
 * The confirm is `withdrawConfirm` — "Yes, take me off this" — and not "Confirm
 * withdrawal", because the second reads like a form and the first reads like a
 * sentence somebody would say.
 *
 * ── AND IT IS NOT A DELETION ────────────────────────────────────────────────────
 *
 * Invariant 6. `withdrawBody` says the record stays and the family can find somebody
 * else, which is both true and the thing the person actually wants to know: that
 * saying no has not left a hole nobody can see.
 *
 * ── AVAILABILITY IS NEVER CONSENT ───────────────────────────────────────────────
 *
 * Invariant 3, AVL-019. Nothing in this block says "book", "assign", "allocate" or
 * "reserve", and nothing implies that offering time has committed anybody to
 * anything. The one sentence that names the consequence of agreeing —
 * `COMMITMENT_MESSAGES.consequence` — already exists and is used as-is.
 */
export const VOLUNTEER_SURFACE_MESSAGES = {
  /**
   * The offer itself.
   *
   * `ACTION_LABELS.commit` already carries "I can do this"; this block does not
   * restate it. What is here is everything around the tap.
   */
  helpNeededHere: "This still needs someone.",
  alreadyHelping: "You're down to help with this.",
  someoneElseHelping: (displayName: string): string => `${displayName} is helping with this.`,

  withdraw: "I can't do this now",
  withdrawTitle: "Take yourself off this?",
  withdrawBody:
    "You'll no longer be marked as doing it, and the family can look for somebody else. Nothing is deleted — the plan keeps a record of what changed.",
  withdrawConfirm: "Yes, take me off this",
  withdrawKeep: "No, I can still do it",
  /** The label says optional, so nobody reads a required field and hesitates. */
  withdrawReasonLabel: "Anything you want to tell the organiser? (optional)",
  withdrawNoReasonNeeded: "You don't have to give a reason.",
  withdrawReasonUnset: "I'd rather not say",

  /** Shown while a claim or a withdrawal is in flight. */
  working: "Just a moment…",

  /**
   * The refresh a stale rejection offers.
   *
   * The bead asks for "this changed since you looked — here is the current version"
   * with a refresh. `REJECTION_MESSAGES.STALE_REVISION` already says the first half
   * in reviewed words; this is the control beside it.
   */
  showLatest: "Show me the latest",
} as const;

export const WINDOW_REJECTION_REASONS = ["empty_window", "crosses_day_boundary"] as const;

export type WindowRejectionReason = (typeof WINDOW_REJECTION_REASONS)[number];

export const WINDOW_REJECTION_MESSAGES = {
  empty_window: {
    title: "Check the end time",
    body: "The end time must be after the start time.",
  },
  crosses_day_boundary: {
    title: "Choose times on the same day",
    body: "A time cannot run overnight. Add a separate time for each day instead.",
  },
} as const satisfies Record<WindowRejectionReason, TitledMessage>;

export const COVERAGE_INELIGIBILITY_REASONS = [
  "stale_need_revision",
  "stale_event_revision",
  "membership_inactive",
  "commitment_not_accepted",
] as const;

export type CoverageIneligibilityReason = (typeof COVERAGE_INELIGIBILITY_REASONS)[number];

export const COVERAGE_INELIGIBILITY_MESSAGES = {
  stale_need_revision: {
    title: "The help needed has changed",
    body: "This person needs to check the latest details and agree again before their help counts.",
  },
  stale_event_revision: {
    title: "The event details have changed",
    body: "This person needs to check the latest details and agree again before their help counts.",
  },
  membership_inactive: {
    title: "This person no longer has access",
    body: "Their help no longer counts toward coverage.",
  },
  commitment_not_accepted: {
    title: "This help is not counted",
    body: "Only help that is currently agreed counts toward coverage.",
  },
} as const satisfies Record<CoverageIneligibilityReason, TitledMessage>;

export const ERROR_MESSAGE_KEYS = [
  "availabilityNotSaved",
  "actionNotSavedOffline",
  "permissionDenied",
  "accessRemoved",
  "inviteExpired",
  "serviceUnavailable",
  "circleClosed",
  "itemUnavailable",
  "routeUnavailable",
] as const;

export type ErrorMessageKey = (typeof ERROR_MESSAGE_KEYS)[number];

export const ERROR_MESSAGES = {
  availabilityNotSaved: {
    title: "Your availability was not saved",
    body: "Your selections are still here. Check your connection and try again.",
    action: "Try again",
  },
  actionNotSavedOffline: {
    title: "This was not saved",
    body: "You’re offline, so nothing was queued. Reconnect, check the latest plan and try again.",
    action: "Check connection",
  },
  permissionDenied: {
    title: "You can’t make this change",
    body: "Your access does not include this action. You can still view the latest plan.",
    action: "View the plan",
  },
  accessRemoved: {
    title: "You no longer have access to this plan",
    body: "If this seems unexpected, contact an organiser.",
    action: "Go to your plans",
  },
  inviteExpired: {
    title: "This invitation has expired",
    body: "Ask an organiser for a new invitation.",
    action: "Back to sign in",
  },
  serviceUnavailable: {
    title: "Here For isn’t available at the moment",
    body: "Nothing new was saved. Check your connection and try again later.",
    action: "Try again",
  },
  circleClosed: {
    title: "This plan is closed",
    body: "You can no longer make changes. Contact an organiser if you need more information.",
    action: "Go to your plans",
  },
  itemUnavailable: {
    title: "This item is no longer available",
    body: "It may have changed or been cancelled. View the plan for the latest information.",
    action: "View the plan",
  },
  routeUnavailable: {
    title: "This page isn’t available",
    body: "Check the address or go back to your plans.",
    action: "Go to your plans",
  },
} as const satisfies Record<ErrorMessageKey, ActionableMessage>;

export const GUIDANCE_MESSAGES = {
  availabilityMeaning: "Availability means you could be asked. It does not book you in.",
  coverageMeaning: "Covered means somebody has actually agreed.",
  conversationLocation: "WhatsApp is where you talk. Here For is where you check what was agreed.",
  practicalNote:
    "Keep this brief and practical. Do not add diagnoses, medication details or anything the whole family does not need to see.",
} as const;

const capabilityCountPhrase = (count: number): string =>
  count === 1 ? "1 capability" : `${String(count)} capabilities`;

/**
 * Text for DEVELOPER-FACING tooling — today, the dev persona switcher
 * (bead hf-e3-convex-boundary-zf7.4.2).
 *
 * WHY IT IS HERE AT ALL. The copy guard covers every TSX file under apps/web, and the
 * switcher is a real component in that tree, so its words have to live in this
 * package. Keeping them here also means nobody has to decide, string by string,
 * whether a given piece of text is "product enough" to be governed — a judgement
 * call that always erodes.
 *
 * WHY IT IS NOT IN `PRODUCT_LANGUAGE`. That catalogue is the complete set of words a
 * FAMILY can read, and the tone review, the glossary and the discouraged-vocabulary
 * scan all run against it. A developer tool that never appears in a family's browser
 * would dilute exactly the thing those checks measure. It is exported separately so
 * both facts stay true: governed by the package, absent from the product catalogue.
 *
 * The tone rules still apply — these words appear in screenshots and in front of the
 * founder — but the vocabulary here is deliberately the vocabulary of the tool, not
 * of the plan.
 */
export const DEVELOPER_TOOL_LABELS = {
  /** Names the bar. It must read as scaffolding, never as a product feature. */
  personaSwitcherTitle: "Development identity",
  personaSwitcherBadge: "Dev only",
  /**
   * The accessible name of the persona control. Screen-reader users get the same
   * "this is a dev tool" signal that the striped border gives everyone else.
   */
  personaSwitcherLabel: "Acting as (development personas)",
  actingAs: "Acting as",
  signedOut: "Nobody signed in",
  signOut: "Sign out",
  /** Shown when the dev server is not serving the identity endpoints. */
  unavailable:
    "The development identity endpoints aren’t responding. Start the app with the Vite dev server.",
  /** Capability counts are a summary, never the list: the server is the authority. */
  capabilitySummary: capabilityCountPhrase,
  /**
   * The one line describing who you are acting as. Assembled here rather than in the
   * view, like every other sentence in this package — a component that concatenates
   * fragments is a component whose wording nobody can review.
   */
  personaSummary: (archetypeLabel: string, capabilityCount: number): string =>
    `${archetypeLabel} · ${capabilityCountPhrase(capabilityCount)}`,
  archetypes: {
    organiser: "Organiser",
    member: "Member",
    viewOnly: "View only",
    proxyRecorder: "Records for others",
  },
  /**
   * The forced-race demo (bead hf-e6-commitments-coverage-udt.4.2).
   *
   * A dev-only page that puts two people's screens side by side and lets both offer
   * to help at once, so the loser's experience can be reviewed and screenshotted
   * without waiting for two real browsers. Labels live here for the same reason the
   * switcher's do: the copy guard covers every TSX file under apps/web, and a page
   * whose words nobody reviewed is how a demo screenshot ends up misquoting the
   * product.
   */
  raceDemo: {
    title: "Forced race — the last place",
    explanation:
      "Two people, one place left. Both offer at once; the second sees what this bead is about.",
    phoneOf: (name: string): string => `${name}’s screen`,
    planStateLabel: "The plan right now",
    reset: "Start again",
  },
} as const;

export const PRODUCT_LANGUAGE = {
  brand: BRAND,
  vocabulary: CANONICAL_VOCABULARY,
  states: USER_VISIBLE_STATES,
  statusContext: STATUS_CONTEXT,
  actions: ACTION_LABELS,
  navigation: NAVIGATION_LABELS,
  availability: AVAILABILITY_MESSAGES,
  commitments: COMMITMENT_MESSAGES,
  withdrawalReasons: WITHDRAWAL_REASON_LABELS,
  reconfirmation: RECONFIRMATION_MESSAGES,
  emptyStates: EMPTY_STATES,
  rejections: REJECTION_MESSAGES,
  windowRejections: WINDOW_REJECTION_MESSAGES,
  coverageIneligibility: COVERAGE_INELIGIBILITY_MESSAGES,
  claimOutcome: CLAIM_OUTCOME_MESSAGES,
  calendarExport: CALENDAR_EXPORT_MESSAGES,
  calendarFeed: CALENDAR_FEED_MESSAGES,
  circleSettings: CIRCLE_SETTINGS_MESSAGES,
  portabilityExport: PORTABILITY_EXPORT_MESSAGES,
  eventSurfaces: EVENT_SURFACE_MESSAGES,
  needSurfaces: NEED_SURFACE_MESSAGES,
  volunteerSurfaces: VOLUNTEER_SURFACE_MESSAGES,
  errors: ERROR_MESSAGES,
  guidance: GUIDANCE_MESSAGES,
} as const;

export type ProductLanguageCatalogue = typeof PRODUCT_LANGUAGE;

/**
 * People and Membership administration (PER-001..PER-015, ADM-009;
 * bead hf-e3b-circle-people-r3z.1).
 *
 * A proxy-only person is described by what they can do, not by what they lack. The
 * access controls also avoid backend words: presets are the simple starting point,
 * while the optional detail names real actions in the family's language.
 */
export const PEOPLE_MANAGEMENT_MESSAGES = {
  heading: "People",
  introduction: "Everyone who is part of this plan, including people who do not sign in.",
  loadingLabel: "Loading people",
  emptyTitle: "Nobody has been added yet",
  emptyBody: "An organiser can add the first person.",

  addAction: "Add a person",
  addTitle: "Add someone to the plan",
  createAction: "Add this person",
  editPersonAction: "Edit details",
  editPersonTitle: "Edit this person",
  editCapabilitiesAction: "Change what they can do",
  editCapabilitiesTitle: "What can this person do?",
  saveCapabilitiesAction: "Save what they can do",
  deactivateAction: "Remove access",
  deactivateTitle: "Remove this person's access?",
  deactivateBody: "They will no longer be able to open or change this plan.",
  deactivateHistoryNotice:
    "Their past help and availability stay in the plan. Removing access does not delete their history.",
  confirmDeactivateAction: "Yes, remove access",
  keepActiveAction: "Keep their access",
  cancelAction: "Cancel",
  saveAction: "Save",

  nameLabel: "Name",
  relationshipLabel: "Relationship",
  relationshipInputLabel: "Relationship to the person this plan is for (optional)",
  emailLabel: "Email address (optional)",
  phoneLabel: "Mobile number (optional)",
  phoneHint: "Include the country code, such as +44.",
  contactMethodsLabel: "Contact details",
  noContactMethods: "No contact details added.",
  contactKinds: { email: "Email", sms: "Text message" },

  proxyOnlyLabel: "Help recorded on their behalf",
  proxyCreationNotice:
    "This person does not need to sign in. An organiser can record availability and help only after the person has agreed.",
  roles: { organiser: "Organiser", member: "Helper" },
  accessStates: {
    pending: "Waiting to join",
    active: "Active",
    suspended: "Paused",
    revoked: "Removed from plan",
  },

  presetLabel: "Starting point",
  presetHint: "Choose the closest fit, then adjust individual actions if needed.",
  presets: {
    organiser: "Organiser",
    helper: "Helper",
    viewsOnly: "Views only",
  },
  fineGrainedSummary: "Adjust individual actions",
  fineGrainedHint: "These choices take effect the next time this person uses the plan.",
  capabilitiesLegend: "What this person can do",
  capabilities: {
    "plan.view": "See the plan",
    "plan.manage": "Change plan settings",
    "plan.share": "Make sharing links",
    "plan.export": "Export the plan",
    "plan.close": "Close the plan",
    "event.create": "Add events",
    "event.edit": "Edit events",
    "event.cancel": "Cancel events",
    "need.create": "Add help needed",
    "need.edit": "Edit help needed",
    "need.cancel": "Cancel help needed",
    "availability.view": "See availability",
    "availability.record.self": "Add their own availability",
    "availability.record.proxy": "Record availability for somebody else",
    "request.send": "Ask a specific person",
    "commitment.accept.self": "Offer to help",
    "commitment.withdraw.self": "Withdraw their own help",
    "commitment.record.proxy": "Record another person's agreement",
    "commitment.complete": "Mark help finished",
    "people.view": "See people",
    "people.manage": "Manage people",
    "people.viewContactDetails": "See full contact details",
    "notes.viewRestricted": "See restricted practical notes",
    "audit.view": "See change history",
  },

  futureCommitments: (count: number): string =>
    count === 1
      ? "This person has 1 future commitment. It stays in the plan after access is removed."
      : `This person has ${String(count)} future commitments. They stay in the plan after access is removed.`,
  saved: {
    created: "Person added.",
    person: "Details saved.",
    membership: "What they can do has been saved.",
    deactivated: "Access removed. Their history is still in the plan.",
  },
  errors: {
    duplicateContact: "Those contact details are already used by someone in this plan.",
    lastOrganiser: "Keep at least one organiser who can manage people.",
    validation: "Check the details and try again.",
    notFound: "That person is no longer available to change.",
    serviceUnavailable: "This change was not saved. Check your connection and try again.",
  },
} as const;

/**
 * Done/cancelled lifecycle copy (hf-e4-events-needs-6r5.4, plan §10.2–§10.3).
 *
 * Appended as a self-contained feature catalogue because product-language is an
 * append-only shared surface. Cancellation says exactly what else closes before the
 * organiser confirms; the helper-facing reason is a fixed code, never free text.
 */
export const LIFECYCLE_SURFACE_MESSAGES = {
  event: {
    markDone: "Mark as happened",
    markDoneTitle: "Mark this as happened?",
    markDoneBody: "Use this after the day, when you know it happened.",
    markDoneConfirm: "Yes, it happened",
    cancelNeedImpact: (needCount: number): string =>
      needCount === 1
        ? "The 1 linked help request will also be cancelled."
        : `All ${String(needCount)} linked help requests will also be cancelled.`,
  },
  helperCancellationReasons: {
    event_cancelled:
      "The event was cancelled, so this help is no longer needed. Your agreement has been released; you do not need to do anything.",
    need_cancelled:
      "This help is no longer needed. Your agreement has been released; you do not need to do anything.",
  },
} as const;

export type LifecycleRejectionReason =
  | "ILLEGAL_TRANSITION"
  | "TERMINAL_STATE"
  | "ALREADY_IN_STATE"
  | "NEED_JUST_COVERED"
  | "NEED_NOT_STARTED"
  | "REOPEN_REQUIRES_EXPLICIT_REVISION"
  | "PUBLISHED_RECORD_MUST_BE_CANCELLED"
  | "INVITATION_NOT_PROVISIONED";

/** Every typed kernel lifecycle refusal, translated without exposing its code. */
export const LIFECYCLE_REJECTION_MESSAGES = {
  ILLEGAL_TRANSITION: "That change no longer fits this item. Check the latest plan.",
  TERMINAL_STATE: "This is already finished or cancelled. Nothing was changed.",
  ALREADY_IN_STATE: "This is already up to date. Nothing else was changed.",
  NEED_JUST_COVERED: "This help has just been covered. Nothing was added.",
  NEED_NOT_STARTED: "This help has not started yet. Wait until afterwards to mark it done.",
  REOPEN_REQUIRES_EXPLICIT_REVISION:
    "Reopening this needs a new version so everyone can check the details again.",
  PUBLISHED_RECORD_MUST_BE_CANCELLED:
    "This stays in the plan’s history. Cancel it instead of deleting it.",
  INVITATION_NOT_PROVISIONED: "This invitation is not ready to share yet.",
} as const satisfies Record<LifecycleRejectionReason, string>;

/**
 * Coverage surfaces: the week, and the organiser's glance (bead
 * hf-e6-commitments-coverage-udt.6; plan §10.1, §16).
 *
 * Appended as a self-contained feature catalogue, per the shared append-only
 * convention for this file.
 *
 * ── WHAT THIS CATALOGUE IS FOR ─────────────────────────────────────────────────
 *
 * One question: open the week and know instantly what is handled and what still
 * needs chasing. Everything here serves that and nothing else.
 *
 * ── THE LINE IT MUST NOT CROSS ─────────────────────────────────────────────────
 *
 * A plan-health panel is the single most likely place in this product for a
 * dashboard to grow — a percentage, a score, a green week, a streak. There is no
 * such thing here and there must not be. Every sentence below counts NEEDS, names
 * what is outstanding, and then stops.
 *
 * So: no praise for a covered week, and no pressure about an uncovered one. A family
 * with two gaps on Thursday has a Thursday problem, not a performance problem, and a
 * panel that congratulated them last week would be scoring them this week. The tally
 * reuses `CANONICAL_VOCABULARY` for its labels precisely so the glance and the chips
 * cannot end up calling the same state two different things.
 */
export const COVERAGE_SURFACE_MESSAGES = {
  weekHeading: "This week",
  planHealthTitle: "How this week looks",
  /**
   * Everything is covered. Said plainly, once, with nothing added.
   *
   * The temptation is "Well done — everything is covered!". That turns a fact into a
   * grade, and a grade implies the weeks it is not said are failures.
   */
  allHandled: "Everything this week is covered.",
  /** No needs at all. Different from covered, and it must not read as an achievement. */
  nothingPlanned: "Nothing needs anybody this week.",
  /** The chase list, as a count of things rather than a warning. */
  needingSomeone: (count: number): string =>
    count === 1 ? "1 thing still needs someone." : `${String(count)} things still need someone.`,
  /**
   * Somebody agreed, something changed, and they have not answered yet.
   *
   * "Needs to be asked again" rather than "is overdue": the person did nothing wrong,
   * the plan moved underneath them.
   */
  awaitingRecheck: (count: number): string =>
    count === 1
      ? "1 person needs to be asked again."
      : `${String(count)} people need to be asked again.`,
  /** A day with nothing on it. Present in the week, and not a gap to fill. */
  dayNothingNeeded: "Nobody is needed.",
  /** Accessible name for the week region, so the landmark says which week. */
  weekRegionLabel: (weekStartLabel: string): string => `The week of ${weekStartLabel}`,

  /* ---- Moving between weeks (bead hf-rtf) ----------------------------------- */

  /**
   * The heading, once the view can move.
   *
   * `weekHeading` above stays, and is still used, for the week that really is this
   * one: "This week" is what a person calls it, and replacing it with a date
   * everywhere would make the common case read like a report. Any other week names
   * itself, because a heading saying "This week" over a week that is not this one is
   * the specific wrongness this bead exists to remove.
   */
  weekOfHeading: (weekStartLabel: string): string => `Week of ${weekStartLabel}`,
  previousWeek: "Previous week",
  nextWeek: "Next week",
  /** Back to the week we are actually in, from wherever the reader wandered. */
  thisWeekAction: "Back to this week",
  /** Tally row label + count, e.g. "Needs someone: 2". Labels come from the vocabulary. */
  tally: (label: string, count: number): string => `${label}: ${String(count)}`,
} as const;

/**
 * Single-need cancellation scope (hf-e4-events-needs-6r5.4, NED-015).
 *
 * Kept separate from event cancellation because these are deliberately different
 * acts: cancelling one help request leaves its linked event and sibling needs alone.
 */
export const LIFECYCLE_NEED_CANCELLATION_MESSAGES = {
  impact: (linkedToEvent: boolean, committedPeopleCount: number): string => {
    const scope = linkedToEvent
      ? "Only this help request will be cancelled. The event stays in the plan."
      : "Only this help request will be cancelled.";
    if (committedPeopleCount === 0) return scope;
    const people =
      committedPeopleCount === 1
        ? "1 person has agreed to help. They'll be told it's no longer needed, and thanked."
        : `${String(committedPeopleCount)} people have agreed to help. They'll be told it's no longer needed, and thanked.`;
    return `${scope} ${people}`;
  },
} as const;

/**
 * The recheck surfaces — the trust loop's words (bead
 * hf-e6-commitments-coverage-udt.5; plan §10.9, COM-015, COM-016).
 *
 * Appended as a self-contained feature catalogue, per the shared append-only
 * convention for this file. `RECONFIRMATION_MESSAGES` above already owns the title,
 * the introduction, the "Was"/"Now" labels and the two answers; nothing here repeats
 * them, and `NEED_SURFACE_MESSAGES.helpModes` supplies the mode names. What is new is
 * the vocabulary for naming WHICH thing moved, and the organiser's side of the loop.
 *
 * ── THE SENTENCE THIS CATALOGUE EXISTS TO AVOID ────────────────────────────────
 *
 * "Please confirm you are still available." That is a request for reassurance, and it
 * puts the burden of the change on the person who did not make it. Plan §10.9 asks
 * for something harder and kinder: tell them exactly what moved, then ask them to
 * decide again — with No as an equally ordinary answer, not a failure to cooperate.
 *
 * ── AND THE ONE IT AVOIDS ON THE ORGANISER'S SIDE ──────────────────────────────
 *
 * "3 people have not responded." That frames helpers as unresponsive when the truth
 * is that a question was asked a moment ago and they are living their lives. The
 * heading is "Still to answer", which describes the QUESTION's state rather than
 * grading the people.
 */
export const RECHECK_SURFACE_MESSAGES = {
  /** Heading over a helper's own outstanding rechecks. */
  listTitle: "Please check these again",
  /** Nothing outstanding. Plain, and not congratulatory. */
  noneOutstanding: "Nothing needs rechecking.",
  /**
   * Named so a helper knows which fact moved before reading two timestamps.
   *
   * "When you're needed" rather than "Window": the need's window is the hours THEY
   * agreed to, and NED-002 exists because people confuse it with the appointment.
   * "The appointment" is deliberately the other phrase, so the two can never read as
   * the same thing.
   */
  changed: {
    needWindow: "When you’re needed",
    requiredCapacity: "How many people are needed",
    requiredHelpModes: "What’s needed",
    eventWindow: "The appointment",
    eventLocation: "Where it is",
  },
  /**
   * The location change, with no values.
   *
   * The revision history stores a fingerprint rather than an address, on purpose
   * (RULE 2, §4.6) — so this says the place moved and sends them to the plan for the
   * detail, rather than showing a hash or, worse, keeping addresses forever.
   */
  locationMoved: "The place has changed. Check the plan for the new details.",
  /** Somebody needed, as a count. Never a fraction, never a bar (COV-006). */
  peopleNeeded: (count: number): string => (count === 1 ? "1 person" : `${String(count)} people`),
  /**
   * Shown when the revision the person agreed to can no longer be read back.
   *
   * Honest rather than reconstructed: a need created before revision history, or a
   * gap left by an erasure. Telling somebody their window moved to a time assembled
   * from whatever was to hand would be worse than telling them nothing — and the
   * renewed Yes/No is still asked, because their agreement is still stale.
   */
  changeUnavailable:
    "The earlier details aren’t available to show. Please check the plan and decide again.",

  /* --------------------------------------------------- the organiser's side --- */

  /** Heading over who has and has not answered a change the organiser made. */
  audienceTitle: "Who you’re waiting on",
  /** The question's state, not a grade on the person. */
  awaitingTitle: "Still to answer",
  reconfirmedTitle: "Said yes again",
  declinedTitle: "No longer helping",
  /**
   * Said when everybody has answered, whichever way they answered.
   *
   * It does NOT say "everyone confirmed" — some of them said no, and a summary that
   * rounded that up would be the product telling an organiser what they want to hear.
   */
  everyoneAnswered: "Everyone has answered.",
  /** Shown to an organiser whose change disturbed nobody. */
  nobodyAffected: "Nobody had agreed to this yet, so there was no one to ask.",

  /* ---- Answering for somebody who is not in the app (bead hf-ww3) ------------ */

  /**
   * The proxy recheck, and the sentence that keeps it honest.
   *
   * The offline member — the WhatsApp-only relative who does the hospital runs — is
   * counted toward coverage (COV-009) and is asked again when a detail changes. They
   * will never open the app to answer, so somebody has to be able to record what they
   * said, exactly as their first agreement was recorded (COM-008, COM-009).
   */
  proxyAnswerAction: (displayName: string): string => `Record ${displayName}’s answer`,
  proxyAnswerTitle: (displayName: string): string => `What did ${displayName} say?`,
  /**
   * Why this asks HOW, every time.
   *
   * The change is the point: they are being asked about something they had not seen
   * when they first agreed, so the earlier conversation cannot be the evidence for
   * this one. Saying so here means an organiser is never surprised by the question.
   */
  proxyAnswerIntroduction:
    "Record what they told you about the change. Ask them again — what they agreed to before was the earlier details.",
  proxyAnswerSourceLabel: "How did you hear from them?",
  proxyAnswerYes: "They said yes to the new details",
  proxyAnswerNo: "They can’t do it now",
  proxyAnswerCancel: "Go back",
  proxyAnswerWorking: "Recording their answer…",
  /** States what the circle will see, before it is recorded rather than after. */
  proxyAnswerConsequence: (displayName: string, source: string): string =>
    `The circle will see that ${displayName} answered ${source}, and that you recorded it.`,
  proxyAnswerRecorded: (displayName: string): string =>
    `${displayName}’s answer has been recorded.`,
} as const;

/**
 * Offline and unsent-availability language (PLT-011/012).
 *
 * The banner names the consequence, not merely the connection state: a person needs
 * to know that the screen will not let an old tap change somebody else's plan. The
 * draft language is equally explicit that saving on this device is not sending.
 */
export const OFFLINE_MESSAGES = {
  offline: "You are offline — actions that affect others are paused.",
  staleReads: "What you can see may be out of date.",
  refreshing: "Back online — refreshing the plan before actions resume.",
  availabilityDraft: {
    title: "Add availability",
    introduction:
      "Save an unsent draft on this device, then review it before you send it to the circle.",
    dateLabel: "Date",
    blockLabel: "Time of day",
    helpModeLabel: "Kind of help",
    blocks: {
      morning: "Morning",
      afternoon: "Afternoon",
      evening: "Evening",
    },
    helpModes: {
      inPerson: "In person",
      driving: "Driving",
      phoneOrOnline: "Phone or online",
    },
    save: "Save as unsent draft",
    notSent: "Not sent yet",
    saved: "This draft is saved only on this device. It is not visible to the circle.",
    send: "Send availability",
    sent: "Your availability has been sent to the circle.",
    invalidDate: "Choose a valid date.",
    storageUnavailable: "This browser could not save the draft on this device.",
    sendFailed: "The draft was not sent. It is still saved on this device.",
  },
} as const;

/**
 * Recording an agreement made outside Here For (COM-007–COM-009, plan §17.13).
 *
 * Proxy commitments are never presented as if the helper pressed the button. The
 * formatter below is deliberately shared by every current surface — and left as
 * the seam the later print view can consume — so the helper, recorder and source
 * cannot drift apart between the detail and week views.
 *
 * This block is self-contained and appended under the shared product-language
 * convention for bead `hf-e6-commitments-coverage-udt.3`.
 */
export const PROXY_COMMITMENT_MESSAGES = {
  action: "Record someone else",
  title: "Record someone’s agreement",
  introduction:
    "Use this after the person has agreed outside Here For. The plan will show who agreed, who recorded it, and how the agreement was given.",
  personLabel: "Who agreed to help?",
  sourceLabel: "How did they agree?",
  sources: {
    whatsapp: "by WhatsApp",
    phone: "by phone",
    in_person: "in person",
  },
  /** Older synthetic fixtures carry descriptive sources; keep those honest too. */
  sourceDescription: (source: string): string => {
    if (source.trim().length === 0) return "outside Here For";
    if (source === "whatsapp") return "by WhatsApp";
    if (source === "phone") return "by phone";
    if (source === "in_person") return "in person";
    return `through ${source}`;
  },
  consequence: (personName: string, source: string): string =>
    `${personName} will be responsible for this help request. The circle will see that you recorded their agreement ${source}.`,
  confirm: "Record this agreement",
  keepChecking: "Go back",
  working: "Recording agreement…",
  success: (personName: string): string => `${personName} is now recorded as helping.`,
  noEligiblePeople: "There is no one else available to record here.",
  /**
   * Canonical visible provenance for cards, detail, tooltips and future print.
   * Both people and the agreement channel stay in the one indivisible string.
   */
  provenance: (helperName: string, recorderName: string, source: string): string =>
    `${helperName} (recorded by ${recorderName}, agreed ${PROXY_COMMITMENT_MESSAGES.sourceDescription(source)})`,
} as const;

/**
 * The could-help matching surface (bead hf-e5-availability-ccp.2; plan §11, §10.1).
 *
 * Appended as a self-contained feature catalogue, per the shared append-only
 * convention for this file.
 *
 * ── THE THREE WORDS THIS CATALOGUE EXISTS TO KEEP APART ────────────────────────
 *
 * COULD HELP is a possibility somebody recorded about their own week.
 * ASKED is a question an organiser chose to send to one named person.
 * AGREED is a promise.
 *
 * They are not degrees of the same thing, and the surface must never let one shade
 * into the next. `AVAILABILITY_MESSAGES.meaning` tells the HELPER "this means the
 * family may ask you — it does not book you in"; everything below is the mirror of
 * that sentence said to the ORGANISER, so the two halves of the product describe the
 * same fact in the same terms.
 *
 * ── AND THE ONE IT REFUSES TO OFFER ────────────────────────────────────────────
 *
 * There is no "ask everyone", no "ask the best match", and no wording that ranks one
 * person above another as more suitable. Matching is CONTAINMENT plus mode plus
 * absence of conflict — a yes-or-no property (§11, AVL-021) — so a list of qualifying
 * people is a list of equals, and copy implying a ranking would invent a judgement the
 * domain does not make and a family should not be handed.
 */
export const MATCHING_SURFACE_MESSAGES = {
  title: "Who could help with this",
  /**
   * The organiser's half of the availability promise.
   *
   * Says the quiet part: appearing here is not agreement, and nothing has been sent.
   */
  meaning:
    "These people said they could help at this time. Nobody has been asked yet, and nobody has agreed.",
  /** Nobody qualifies. Stated as a fact about the window, never as a failing. */
  noneTitle: "Nobody has said they could help at this time",
  noneBody:
    "People add times when they could help. You can still ask anyone in the circle directly.",
  /** The action, per candidate and never in bulk. */
  ask: (displayName: string): string => `Ask ${displayName}`,
  /*
   * `askNotReady` USED TO LIVE HERE, and its removal is deliberate.
   *
   * It was the honest stand-in ccp.2 showed while direct requests were being built —
   * "asking from here is coming soon". `hf-e6-commitments-coverage-udt.1` shipped the
   * ask, so the sentence has no surface left to render it, and a retired string in a
   * copy catalogue is a sentence somebody will eventually put back on a screen.
   *
   * Whether the ask appears is now a capability question, answered by the server. A
   * member who may not ask is offered no control at all — not a message explaining
   * that the product is not ready, which would have been untrue.
   */
  /**
   * Provenance on a proxy-recorded window (AVL-011, COM-009).
   *
   * Who recorded it and how, wherever the availability is shown — because an organiser
   * deciding whom to ask should know whether the person said this themselves or
   * somebody wrote it down for them.
   */
  recordedBy: (recorderName: string): string => `Recorded by ${recorderName}`,
  recordedBySource: (recorderName: string, source: string): string =>
    `Recorded by ${recorderName}, from ${source}`,
  /** Freshness, so an organiser can weigh a week-old note against this morning's. */
  recordedOn: (dateLabel: string): string => `Added ${dateLabel}`,
} as const;

/**
 * Availability entry and management (AVL-001, AVL-003, AVL-007, AVL-011–012,
 * AVL-015, AVL-018–019).
 *
 * Availability is a possibility, never an agreement. Keep this catalogue separate
 * from commitments language so no surface can accidentally turn an offered time
 * into a responsibility.
 */
export const AVAILABILITY_SURFACE_MESSAGES = {
  loadingLabel: "Loading availability",
  entryHeading: "Add times you could be asked about",
  meaning:
    "These are times when the circle may ask if you can help. Adding them does not book you in or make a commitment.",
  subjectLegend: "Whose availability is this?",
  subject: {
    self: "Mine",
    proxy: "Someone else’s",
  },
  weekStartLabel: "Week starting",
  weekRegion: (start: string, end: string): string => `Choose dates from ${start} to ${end}`,
  explicitEnd: (end: string): string => `This availability ends on ${end}.`,
  ends: (end: string): string => `Ends ${end}`,
  entryModeLegend: "Choose times",
  blockMode: "Use the circle’s time blocks",
  exactMode: "Enter exact times",
  blockNames: {
    morning: "Morning",
    afternoon: "Afternoon",
    evening: "Evening",
  },
  blockAccessibleName: (date: string, block: string, bounds: string): string =>
    `${date}, ${block}, ${bounds}`,
  exactStartLabel: "Starts",
  exactEndLabel: "Ends",
  helpModeLegend: "Kinds of help you could be asked about",
  helpModes: {
    inPerson: "In person",
    driving: "Driving",
    phoneOrOnline: "Phone or online",
  },
  proxyHeading: "Record availability for someone else",
  proxyMeaning:
    "Use this only after the person has shared these times with you. The circle will see who recorded them and how they were shared.",
  proxyPersonLabel: "Whose times are these?",
  proxySourceLabel: "How did they share these times?",
  proxySources: {
    message: "by message",
    phone: "by phone",
    in_person: "in person",
  },
  proxySourceDescription: (source: string): string => {
    if (source === "message") return "by message";
    if (source === "phone") return "by phone";
    if (source === "in_person") return "in person";
    return `through ${source}`;
  },
  proxyConsent: "I confirm that this person shared these times with me.",
  proxyEditHeading: "Edit someone else’s availability",
  proxyEditConsent: "I confirm that this change reflects what the person told me.",
  proxyWithdrawalHeading: "Withdraw someone else’s availability",
  proxyWithdrawConsent: "I confirm that this person asked me to withdraw these times.",
  saveSelf: "Add my availability",
  saveProxy: "Record their availability",
  saved: "Availability added. The circle can now see these times.",
  overviewHeading: "Times people could be asked about",
  emptyTitle: "No availability has been added for this week",
  emptyBody: "People can add times when they could be asked about helping.",
  provenance: (person: string, recorder: string, source: string): string =>
    `${person} — recorded by ${recorder}, shared ${source}`,
  edit: "Edit availability",
  editHeading: (person: string): string => `Edit ${person}’s availability`,
  editMeaning: "Shortening or changing availability does not change any commitments.",
  editWindow: (number: number): string => `Time ${String(number)}`,
  saveChanges: "Save changes",
  updated: "Availability updated.",
  withdraw: "Withdraw availability",
  withdrawHeading: "Withdraw these times?",
  withdrawMeaning:
    "These times will no longer be shown as available. Any commitments stay in place.",
  severalDates: "these dates",
  commitmentsStay: (count: number, date: string, person?: string): string =>
    `${person ?? "You"} still ${person === undefined ? "have" : "has"} ${String(count)} ${count === 1 ? "commitment" : "commitments"} on ${date} — ${count === 1 ? "it stays" : "they stay"} unless you step back from ${count === 1 ? "it" : "them"} too.`,
  confirmWithdraw: "Withdraw availability",
  cancel: "Keep availability",
  withdrawn: "Availability withdrawn. Commitments were not changed.",
  errors: {
    choosePerson: "Choose the person whose availability you are recording.",
    confirmProxyConsent: "Confirm that the person shared these times with you.",
    invalid_week_start: "Choose a valid week.",
    week_must_start_monday: "Choose a week that starts on Monday.",
    choose_time: "Choose at least one date and time.",
    choose_help_mode: "Choose at least one kind of help.",
    date_outside_week: "Choose a date in the selected week.",
    exact_time_unreadable: "Choose valid start and end times.",
    overnight_not_supported:
      "Availability cannot run overnight. Add separate daytime times instead.",
    end_not_after_start: "The end time must be after the start time.",
    serviceUnavailable: "That change could not be saved. Please check the plan and try again.",
  },
} as const;

/**
 * Direct requests — asking one named person, and every way that question can end
 * (REQ-001 through REQ-011, plan §10.4; bead hf-e6-commitments-coverage-udt.1).
 *
 * ── EVERY SENTENCE HERE DEFENDS THE SAME INVARIANT ─────────────────────────────
 *
 * A direct request contributes ZERO coverage until it is accepted (invariant 3).
 * The code enforces that — `deriveCoverage` has no argument for a request, so a
 * pending ask is not merely uncounted but uncountable. What copy can still get wrong
 * is the FEELING, and that is what this catalogue is for: an organiser who reads
 * "Asked Mary" and relaxes has been misled by words even though the number was right.
 *
 * So `asked` never appears without what it is not. `sent` says "not covered yet" in
 * the same breath. `awaitingHeading` says "waiting to hear". Nowhere does a sent ask
 * get a tick, a green tone, or a sentence that could be read as arranged.
 *
 * ── THE FOUR ENDINGS, AND WHY NONE OF THEM BLAMES ANYBODY ──────────────────────
 *
 * ACCEPTED is the only one that changes the plan. The other three — declined,
 * lapsed, withdrawn — return the need to exactly where it was, and each has a
 * DIFFERENT sentence because they are different events in a family's week:
 *
 *   DECLINED is somebody answering honestly. §10.10's no-fault rule is not only
 *   about withdrawal: "can't" is an answer, not a failure, and the copy states the
 *   consequence for the plan rather than commenting on the person. There is no "sorry
 *   to hear that", which would make a plain no feel like it needed an apology.
 *
 *   LAPSED is nobody's fault at all — the silence may mean a phone in a drawer. The
 *   organiser is told plainly and offered the next move in the same sentence, because
 *   the entire risk of expiry is a need quietly stranded behind an ask nobody
 *   remembers sending. The helper is never told they failed to reply.
 *
 *   WITHDRAWN is the organiser tidying up, so the wording is about the question, not
 *   about the person: the ask stops standing, and the helper owes nothing.
 *
 * ── AND THE NUDGE ──────────────────────────────────────────────────────────────
 *
 * `stillWaiting` is the one message that goes to somebody who has not acted, so it is
 * the one with the greatest capacity to feel like pressure. It names the person
 * waiting, states the deadline as a fact, and stops. No count of how many times they
 * have been reminded, no "please respond", no urgency styling — the product has no
 * streaks and no guilt mechanics, and a reminder is the easiest place to grow one.
 */
export const DIRECT_REQUEST_MESSAGES = {
  /* ---- The organiser's side: sending, and watching --------------------------- */

  /** Confirmation before sending, so "Ask Mary" is never a one-tap accident. */
  confirmHeading: (displayName: string): string => `Ask ${displayName} about this?`,
  /**
   * What sending does and — the important half — what it does not do.
   *
   * The mirror of `AVAILABILITY_MESSAGES.meaning` said to the organiser: the helper
   * was promised that availability does not book them in, and this is the screen
   * where the organiser learns the same fact from the other side.
   */
  confirmMeaning: (displayName: string): string =>
    `${displayName} will be asked if they can do this. It does not book them in, and this still needs someone until they say yes.`,
  confirmSend: "Send the ask",
  confirmCancel: "Don’t ask",
  sent: (displayName: string): string =>
    `${displayName} has been asked. This still needs someone until they reply.`,

  /** The organiser's four lists on a need. Headings state facts, never scores. */
  audienceHeading: "Who has been asked",
  awaitingHeading: "Waiting to hear",
  lapsedHeading: "No reply in time",
  declinedHeading: "Can’t this time",
  acceptedHeading: "Said yes",
  /**
   * The lapse, and the next move in the same sentence (REQ-009).
   *
   * A lapsed ask that only disappeared would leave the organiser believing somebody
   * was still considering it. This is why expiry is never silent.
   */
  lapsed: (displayName: string): string =>
    `Your ask to ${displayName} lapsed — nobody replied in time. You can ask someone else.`,
  declinedByPerson: (displayName: string): string => `${displayName} can’t this time.`,
  /** Taking the question back. About the ask, never about the person. */
  withdrawAsk: (displayName: string): string => `Withdraw the ask to ${displayName}`,
  withdrawn: (displayName: string): string =>
    `The ask to ${displayName} has been withdrawn. They no longer need to reply.`,
  noneAsked: "Nobody has been asked about this yet.",

  /* ---- Asking somebody who never recorded a time (REQ-001; bead hf-mtk) ------ */

  /**
   * The second way to ask, and the reason it exists.
   *
   * The could-help list only holds people whose recorded availability contains this
   * window (AVL-021), and the members least likely to record availability are exactly
   * the ones an organiser most often needs to ask (§7.2). `MATCHING_SURFACE_MESSAGES`
   * already told organisers they "can still ask anyone in the circle directly"; this
   * is the surface that makes that sentence true.
   */
  anyoneHeading: "Ask someone else",
  /**
   * The same two things the could-help list says, in the same order: what this list
   * is, and what asking does not do. One at a time is stated rather than merely
   * enforced, because somebody about to read a list of names should know before they
   * start that there is no way to select several (AVL-026, REQ-010).
   */
  anyoneMeaning:
    "Anyone in the circle can be asked, whether or not they have said when they could help. One person at a time.",
  /** Everybody is already holding a question about this need. A fact, not a dead end. */
  anyoneNoneTitle: "There is nobody else to ask",
  anyoneNoneBody: "Everyone else in the circle has already been asked about this one.",

  /* ---- The helper's side: being asked, and answering ------------------------- */

  /** The ask itself, in the shape a person reads on their own screen. */
  askHeading: (askerName: string): string => `${askerName} asked if you could help`,
  /**
   * The consequence of saying yes, spelled out BEFORE the button (COM-001).
   *
   * Identical in substance to `COMMITMENT_MESSAGES.consequence`, because accepting an
   * ask and volunteering create the same commitment through the same guard. A person
   * must not learn different things about the same promise from two screens.
   */
  acceptMeaning: "Saying yes marks you as responsible for this. The family will see your name.",
  /** Answering is two plain choices. No "maybe", no "remind me later". */
  accept: "Yes, I can do this",
  decline: "No, I can’t this time",
  /** No-fault, and about the plan rather than about them (§10.10). */
  declined: "Thanks for letting them know. The family can now ask someone else.",
  accepted: "You’re marked as doing this. Thank you.",
  /** Stated as a fact, so an ask visibly has a horizon rather than hanging forever. */
  expiresOn: (dateLabel: string): string => `Please reply by ${dateLabel}.`,
  /** The pre-expiry nudge (Epic 7 matrix). Names who is waiting, then stops. */
  stillWaiting: (askerName: string): string => `${askerName} is still waiting to hear from you.`,
  /**
   * Heading over the asks waiting for THIS person, on their own screen.
   *
   * Separate from `audienceHeading` on purpose, and the separation is not cosmetic.
   * "Who has been asked" is the organiser looking at a need and reading a list of
   * other people; a helper reading those same words over their own inbox would be
   * told they are looking at somebody else's business. The helper's list is one
   * thing — questions addressed to them — and it says so.
   */
  yourAsksHeading: "Waiting for your answer",
  /** Title over the calm empty state. States the fact; asks for nothing. */
  noneForYouTitle: "Nothing to answer",
  /** No open asks. A calm empty state, never an invitation to go looking for work. */
  noneForYou: "Nobody has asked you to do anything.",

  /* ---- Refusals only an ASK can produce ------------------------------------- */

  /**
   * THE LATE YES (REQ-007) IS DELIBERATELY NOT HERE.
   *
   * Somebody saying yes after the last place went reads
   * `REJECTION_MESSAGES.NEED_JUST_COVERED` — "Someone else just stepped in · This has
   * already been covered. Thank you for checking." — the same reviewed sentence the
   * volunteer flow uses, because it is the same event. A second, warmer wording here
   * would be two sentences for one moment, and they would drift.
   *
   * These three are the refusals that only exist because an ask is a QUESTION rather
   * than a claim, so there is nothing in the shared vocabulary to reuse.
   */
  refusals: {
    /** The ask stopped standing before they answered. Neutral about why. */
    noLongerOpen: {
      title: "This ask is no longer open",
      body: "It was answered, withdrawn or ran out of time. Nothing is needed from you.",
      action: "View the plan",
    },
    /**
     * Already asked, and still waiting.
     *
     * Names the person and states the fact, then stops. It does NOT offer to ask
     * again — a second identical ask is not dangerous, but it reads to the recipient
     * as pressure, and the organiser is better served by being reminded they already
     * did it.
     */
    alreadyAsked: (displayName: string): ActionableMessage => ({
      title: `${displayName} has already been asked`,
      body: "They haven’t replied yet. You can ask someone else in the meantime.",
      action: "View the plan",
    }),
    /**
     * The WhatsApp-only member (§7.2).
     *
     * The whole message is a signpost, not a refusal: they can absolutely help, and
     * the organiser already knows how to reach them. Sending an in-app ask into a
     * mailbox nobody opens would look like progress and be silence.
     */
    notReachable: (displayName: string): ActionableMessage => ({
      title: `${displayName} can’t be asked from here`,
      body: "Ask them the way you usually do, then record what they said.",
      action: "Record someone else",
    }),
    /**
     * The help has already begun (bead hf-gzv).
     *
     * A signpost rather than a refusal, in the same shape as `notReachable`: the
     * organiser is not being told no, they are being told that an in-app question
     * with a reply-by date is the wrong tool for something happening now. Both paths
     * it names are real and already built — record what somebody agrees when you
     * reach them (COM-008), or raise a need for the part still uncovered, which is
     * what §10.6 says to do when a helper can only cover some of a window.
     *
     * No apology and no blame: asking late is the organiser noticing a gap, which is
     * the thing this product most wants them to keep doing.
     */
    alreadyStarted: {
      title: "This help has already started",
      body: "An ask waits for a reply, so it is no use once something is under way. Reach someone the way you usually would and record what they say, or add a need for the time still uncovered.",
      action: "Record someone helping",
    },
  },
} as const;

/**
 * Fridge-door print language (PRT-001/003/005; bead hf-e8-sharing-export-x1v.2).
 *
 * The default is described as discreet, not “redacted”: it is a useful household
 * schedule whose detail has been deliberately minimised, not a broken copy of a more
 * important screen. Full detail is an explicit preview choice, and the contact line
 * remains a second choice because paper can be seen by people outside the circle.
 */
export const PRINT_SCHEDULE_MESSAGES = {
  heading: "Print the plan",
  introduction:
    "Preview what will be put on paper. The discreet version is selected first because a household printout may be seen by visitors.",
  loading: "Preparing the printable view…",
  unavailable: "This printable view isn’t available for this membership.",

  detailLegend: "What to show",
  discreetLabel: "Discreet",
  discreetHint:
    "Shows safe titles, first names, times and coverage. It leaves out locations and contact details.",
  fullLabel: "Full family detail",
  fullHint:
    "Shows the plan’s private titles, help wording, full chosen names and locations. Check the preview before printing.",

  periodLegend: "How many days",
  sevenDaysLabel: "7 days",
  sevenDaysHint: "One fridge-door week.",
  fourteenDaysLabel: "14 days",
  fourteenDaysHint: "This may use more than one sheet of paper.",

  includeContactLabel: "Include the contact line",
  includeContactHint:
    "This is off unless you choose it. Only include it when the paper will be kept somewhere suitable.",
  printAction: "Print this schedule",

  scheduleHeading: (planName: string): string => `${planName} — household schedule`,
  period: (days: 7 | 14, firstDay: string, lastDay: string): string =>
    `${String(days)} days · ${firstDay} to ${lastDay}`,
  discreetNeedLabel: "Help needed",
  eventFallbackTitle: "Plan item",
  emptyDay: "Nothing planned.",
  contactHeading: "Contact",
  helpersHeading: "Who is helping",
  printedAt: (dateAndTime: string): string => `Printed ${dateAndTime}.`,
  freshness: (planName: string): string => `Plans may change; ${planName} is the latest version.`,
} as const;

/** hf-e5-availability-ccp.3: dated expiry and voluntary renewal. */
export const AVAILABILITY_EXPIRY_MESSAGES = {
  endingHeading: (dateLabel: string): string => `Your listed times end on ${dateLabel}`,
  endedHeading: (dateLabel: string): string => `Your listed times ended on ${dateLabel}`,
  promptBody:
    "Would you like to add more times you could be asked about? Nothing is added automatically.",
  addMore: "Add more times",
  expiredHistory: (dateLabel: string): string =>
    `Past availability — this ended on ${dateLabel} and is no longer used for matching.`,
} as const;

/**
 * The helper's own screen (bead hf-e6-commitments-coverage-udt.9, plan §13.4).
 *
 * ── WHY THIS VOCABULARY EXISTS AT ALL ──────────────────────────────────────────
 *
 * Every other coverage surface in the product speaks to the organiser: plan health,
 * the week as coverage, the shortfall list. This one speaks to the person who got a
 * text link and gives the app ninety seconds. The words therefore do two things and
 * refuse a third.
 *
 * They ORIENT — "here is what needs an answer, here is what you already said yes to,
 * here are the times you listed" — and they STOP. There is no summary of how much
 * somebody has done, no comparison with anybody else, no count of what is left. A
 * sentence like "you have 3 outstanding items" would be a workload, and this product
 * does not issue workloads to a family.
 *
 * And they never PRESS. Nothing here is a badge, a streak, a total or a deadline in
 * red. Section headings are nouns and plain statements; an empty section says the
 * restful thing rather than an inviting one, because "nobody has asked you to do
 * anything" is a complete and welcome sentence for somebody who has had a long week.
 *
 * ── THE HEADLINE TIME IS THE SUPPORT WINDOW ────────────────────────────────────
 *
 * `neededFrom` is the need's own window — the hours somebody agreed to — and
 * `aroundEvent` is the appointment, phrased so it can only read as context. NED-002
 * exists because people confuse the two, and a helper's own screen is the exact place
 * where confusing them makes somebody arrive at the wrong time.
 */
export const MY_WEEK_MESSAGES = {
  heading: "My week",
  /** Names the two halves of the screen, in the order they appear. */
  introduction: "What’s waiting for your answer, and what you’ve agreed to.",
  loadingLabel: "Loading your week",

  /* ---- What this person has agreed to ---------------------------------------- */

  /** A promise they made, in their own words. Never "assigned" or "your tasks". */
  commitmentsHeading: "You’ve agreed to",
  commitmentsEmptyTitle: "Nothing agreed yet",
  /**
   * Restful, and deliberately not a prompt.
   *
   * "You haven't agreed to anything yet" states a fact and stops. An empty state that
   * said "find something to help with" would turn a calm screen into a request, which
   * is the pressure mechanic this whole surface is designed without.
   */
  commitmentsEmpty: "You haven’t agreed to anything yet.",
  /** The SUPPORT window — the hours they agreed to, never the appointment's (NED-002). */
  neededFrom: (windowLabel: string): string => `You’re needed ${windowLabel}`,
  /** The appointment, as context. Phrased so it cannot be read as the time to turn up. */
  aroundEvent: (title: string): string => `Around ${title}`,
  /** The other people helping. A fact about company, never a comparison. */
  alsoHelping: (names: string): string => `With ${names}`,

  /* ---- The times they listed ------------------------------------------------- */

  availabilityHeading: "Times you could be asked about",
  availabilityEmptyTitle: "No times listed",
  availabilityEmpty: "You haven’t listed any times you could be asked about.",
  /** How far their listed times reach. A horizon, not a countdown. */
  availabilityUntil: (dateLabel: string): string => `You’ve listed times up to ${dateLabel}.`,
  /** How many separate windows stand behind that date. */
  availabilityWindowCount: (count: number): string =>
    count === 1 ? "1 time listed" : `${String(count)} times listed`,
  /** To the full availability screen, where times are added and changed. */
  viewAvailability: "See and change your times",
} as const;

/**
 * User-previewed WhatsApp-adjacent snippets (SHA-001–012; bead x1v.5).
 *
 * This is an invitation to look, never a demand to act. The default contains only
 * the supported person's first chosen-name word, the need's own window and an
 * authenticated navigation link. Private need/event words never enter the template.
 */
export const SHARE_SNIPPET_MESSAGES = {
  action: "Share this request",
  dialogTitle: "Share this request",
  preview:
    "Check and edit these words before sharing. The link opens the private plan and still requires access.",
  messageLabel: "Message to share",
  nativeShare: "Share from this device",
  copyMessage: "Copy message",
  copyLink: "Copy link",
  close: "Keep this here",
  shared: "The share sheet is open.",
  messageCopied: "Message copied. Paste it where you choose.",
  linkCopied: "Link copied. It still requires plan access.",
  copyUnavailable: "Copy isn’t available in this browser. You can select the message instead.",
  shareUnavailable: "The share sheet didn’t open. You can copy the message instead.",
  invalidLink: "This request can’t be shared from here.",
  defaultMessage: (
    firstName: string,
    dateLabel: string,
    windowLabel: string,
    link: string,
  ): string =>
    `Could anyone help ${firstName} on ${dateLabel}, ${windowLabel}? See the latest plan: ${link}`,
} as const;

/**
 * Short in-circle operational updates (D-081; bead hf-e8-sharing-export-x1v.4).
 *
 * These words keep the surface useful without inviting a clinical history. Proxy
 * provenance stays an indivisible person/recorder/source sentence, matching the
 * trust-preserving formatter seam used by commitments.
 */
export const PRACTICAL_UPDATE_MESSAGES = {
  heading: "Practical updates",
  introduction:
    "Short details that help the circle with this need. Keep medical histories, diagnoses and intimate details somewhere more suitable.",
  empty: "No practical updates have been added.",
  addHeading: "Add a practical update",
  textLabel: "What would help the circle know?",
  textHint: "Keep it short and practical.",
  includeInPrint: "Include this in the full family printout",
  includeInPrintHint: "It stays out of the discreet printout.",
  add: "Add update",
  save: "Save changes",
  edit: "Edit",
  cancel: "Keep as it is",
  remove: "Remove words",
  removed: "This update’s words have been removed. Its history is kept.",
  pinned: "Pinned",
  pin: "Keep this update first",
  printIncluded: "Included in the full family printout",
  history: "See edit history",
  historyHeading: "Edit history",
  historyEmpty: "No recorded changes yet.",
  created: "Added",
  updated: "Changed",
  redacted: "Words removed",
  formerMember: "A former circle member",
  recorded: (name: string, dateLabel: string): string => `Added by ${name} on ${dateLabel}.`,
  proxyProvenance: (person: string, recorder: string, source: string): string =>
    `${person} (recorded by ${recorder}, shared by ${source})`,
  historyEntry: (action: string, actor: string, dateLabel: string): string =>
    `${action} by ${actor} on ${dateLabel}.`,
  printHeading: "Practical updates",
  validation: "Add a short practical update of 500 characters or fewer.",
  unavailable: "That practical update could not be changed. Refresh and try again.",
} as const;

/** Labels for the two D-081 capability switches introduced with practical updates. */
export const PRACTICAL_UPDATE_CAPABILITY_MESSAGES = {
  "practical.write.self": "Add their own practical updates",
  "practical.write.proxy": "Record practical updates for somebody else",
} as const;

/**
 * Notification preferences (NTF-017/018; bead hf-e7-notifications-742.3).
 *
 * ── THE HARDEST SENTENCE IN THIS FILE ──────────────────────────────────────────
 *
 * Four categories cannot be switched off, and the words that say so have to do
 * something difficult: explain a limit without sounding like an excuse for it, and
 * without implying the person asking was trying to get out of something.
 *
 * So the copy never says "required", "mandatory" or "you must". It says what the
 * message IS — a question somebody is waiting on an answer to — and offers the control
 * that does exist: which channel it arrives on. "You can choose how these reach you"
 * is a true and useful sentence; "these cannot be turned off" on its own is a door
 * being closed in somebody's face.
 *
 * ── AND THE QUIET-HOURS POLICY IS SHOWN, NOT DISCOVERED ────────────────────────
 *
 * NTF-018 requires the override to be a visible circle decision. Visible means a
 * member reads it here, in advance, in plain words — not at 03:00 when their phone
 * goes off. Both states get a sentence, because "your circle has NOT turned this on"
 * is exactly as much information as the other way round.
 */
export const NOTIFICATION_PREFERENCE_MESSAGES = {
  heading: "How we reach you",
  introduction:
    "Choose how each kind of message arrives. Nothing here changes what the family can see.",
  loadingLabel: "Loading your notification settings",

  /* ---- The categories, in the words a member would use ----------------------- */

  categories: {
    askedOfYou: {
      title: "Someone asks you to help",
      body: "A question addressed to you, with a date to reply by.",
    },
    answersToYourAsks: {
      title: "Replies to asks you sent",
      body: "Whether the person you asked can help, and when nobody replied in time.",
    },
    reconfirmationAsk: {
      title: "Something you agreed to has changed",
      body: "A renewed choice, with what moved shown beside it.",
    },
    coverageChanged: {
      title: "Someone can no longer help",
      body: "So the plan can find someone else in time.",
    },
    /**
     * Said from the helper's side, and deliberately not as an apology.
     *
     * The whole purpose of this message is that somebody does not set off. So the
     * title states the fact and the body states the consequence — nothing is being
     * asked of them, and nothing about the cancellation is their business to resolve.
     */
    cancellations: {
      title: "Something you agreed to was called off",
      body: "There is nothing to do. You do not need to go.",
    },
    practicalUpdates: {
      title: "Practical details added",
      body: "A note that something useful was added. The note itself stays in the plan.",
    },
    reminders: {
      title: "Reminders",
      body: "The evening before something you agreed to, and needs coming up without anyone.",
    },
  },

  /* ---- Switching off, and the four that stay on ------------------------------ */

  muteLabel: "Send me these",
  /**
   * The unmutable explanation. Names what the message is, then offers the real control.
   *
   * Deliberately NOT "you can't turn this off": that is the same fact said as a
   * refusal, and it invites somebody to feel managed by an app they agreed to help
   * their family with.
   */
  alwaysOnLabel: "Always sent",
  alwaysOnReason:
    "Someone is waiting on your answer to this one, so it always reaches you. You can still choose how.",
  /**
   * The always-on explanation for a category where NOBODY is waiting on an answer.
   *
   * `alwaysOnReason` above is true of the four categories that existed when it was
   * written — an ask, an answer, a recheck, a gap somebody must fill all turn on
   * somebody's reply. It is NOT true of a cancellation, which asks nothing at all, and
   * a row telling a member that somebody awaited their answer to it would be plainly
   * false on the one screen where they decide how to be reached.
   *
   * So the sentence gives the real reason instead, which is a stronger one: the message
   * exists so they do not set off for something that is not happening. Same shape, same
   * offer of the choice that IS theirs, no invented obligation.
   */
  alwaysOnReasonByCategory: {
    cancellations:
      "Nothing is being asked of you. It always reaches you so you do not set off for something that is not happening. You can still choose how.",
  },

  /* ---- Channels -------------------------------------------------------------- */

  channelsLegend: "How it arrives",
  channels: { sms: "Text message", email: "Email" },
  noChannelWarning:
    "You've turned off every way of reaching you for this. Choose at least one, or you won't see it.",

  /* ---- Quiet hours ----------------------------------------------------------- */

  quietHoursHeading: "Quiet hours",
  quietHoursBody:
    "Messages that arrive in these hours are held and delivered when they end. Nothing is dropped.",
  quietHoursStart: "From",
  quietHoursEnd: "Until",
  quietHoursNone: "No quiet hours set.",
  clearQuietHours: "Remove quiet hours",
  /** NTF-018, and the member reads it in advance rather than at 3am. */
  overrideAllowed:
    "Your circle has agreed that an urgent same-day question may arrive during your quiet hours. Only two kinds qualify: a change to something you agreed to, and an ask that is about to run out of time.",
  overrideNotAllowed: "Your circle has not turned this on, so nothing arrives during these hours.",
  /** Shown to whoever may change the circle's policy. */
  circlePolicyLegend: "For the whole circle",
  circlePolicyLabel: "Let urgent same-day questions arrive during quiet hours",
  circlePolicyHint:
    "This applies to everyone. It is shown to every member in their own settings, and every time it is used is recorded.",

  /* ---- Saving ---------------------------------------------------------------- */

  save: "Save these settings",
  saved: "Saved.",
  saveFailed: "That couldn't be saved. Nothing has changed.",
} as const;

/**
 * Correcting and confirming the supported person's name (D-091; bead hf-grr).
 *
 * Saving has two consequences, so the hint says both before the organiser acts:
 * every consumer receives the spelling, and the development stand-in notice goes
 * away. There is no urgency or blame attached to either state.
 */
export const SUPPORTED_PERSON_NAME_MESSAGES = {
  label: "Name for the person this plan is for",
  hint: "Saving confirms this spelling everywhere the name appears and removes the stand-in notice.",
  action: "Save this spelling",
  validation: "Enter the name as the family wants it shown.",
} as const;

/** Per-circle unanswered-ask policy (REQ-009; bead hf-qhx). */
export const DIRECT_REQUEST_EXPIRY_POLICY_MESSAGES = {
  settingsLegend: "How long an unanswered ask stays open",
  settingsLabel: "Days to wait for a reply",
  settingsHint: "This applies to new asks. An ask already sent keeps the reply date it has now.",
  settingsAction: "Save the reply window",
  validation: "Choose a whole number from 1 to 14 days.",
  activePolicy: (days: number): string =>
    `New asks stay open for ${days === 1 ? "1 day" : `${String(days)} days`}, unless the help starts sooner.`,
  atAsk: (days: number): string =>
    `If they do not reply, this ask will lapse after ${days === 1 ? "1 day" : `${String(days)} days`} or when the help starts, whichever comes first.`,
} as const;

/** Neutral outbound subject while D-091 still marks the supported name as a stand-in. */
export const SUPPORTED_PERSON_PLACEHOLDER_SHARE_MESSAGES = {
  neutralSubject: "the person this plan is for",
} as const;

/**
 * The supported person's own printed sheet (PRT-002, PRT-004; plan §20.3, §20.4;
 * bead hf-r75).
 *
 * ── THIS IS THE ONLY SURFACE IN THE PRODUCT ADDRESSED TO HER ───────────────────
 *
 * Everything else here speaks to the circle *about* her. This sheet speaks *to*
 * her, on paper, in a kitchen, probably without a phone or a screen anywhere near
 * it. Three things follow from that and are easy to undo by accident.
 *
 * IT USES HER NAME AND NOTHING ELSE'S. `heading` is "Rowan's week", never the plan
 * title — "Here for Rowan" is the sentence the family uses about her, and a document
 * handed to somebody should not be titled with other people's description of them
 * (§25.12, D-082). Where no name has been recorded it heads the page neutrally
 * rather than guessing a spelling (D-091).
 *
 * IT NEVER COUNTS ANYTHING. There is no coverage vocabulary in this catalogue at
 * all: no "1 of 2", no "needs someone", no state names. Those words exist so a
 * family can find gaps, and a gap on this sheet would read as a person being told
 * that not enough people want to help her. `whoIsComing` names people; that is the
 * entire status model of this page.
 *
 * AND IT PROMISES ONLY WHAT IS AGREED. `stillBeingChecked` exists so an arrangement
 * awaiting reconfirmation can appear without being stated as settled — paper cannot
 * correct itself later (§20.6), so the qualification has to travel on the page.
 */
export const SUPPORTED_PERSON_PRINT_MESSAGES = {
  /**
   * THE ORGANISER'S CONTROLS. These describe both documents to the person choosing
   * between them, so they may — and must — use the family's vocabulary: somebody
   * picking a sheet needs to be told which one leaves the cover out.
   */
  audienceLegend: "Who this sheet is for",
  familyLabel: "The family",
  familyHint: "The household schedule, with who is helping and where cover is still open.",
  supportedPersonLabel: "The person this plan is for",
  supportedPersonHint:
    "A large-print sheet of who is coming and when. It leaves out cover, offers and unanswered asks.",
  contactPersonLabel: "Who to check with",
  contactPersonHint:
    "One person they can ring. It is printed on the sheet exactly as you type it, and is not saved.",
  printAction: "Print this sheet",

  /**
   * EVERYTHING THAT REACHES THE PAPER, AND NOTHING ELSE.
   *
   * Nested rather than merely conventionally named, so "is this printed on her sheet"
   * is a question the code answers instead of a rule a reviewer has to hold. The
   * component renders from this object alone, and the tone test sweeps this object
   * alone for coverage vocabulary — a sweep over the whole catalogue would have to
   * tolerate the organiser's controls above and would then prove nothing about the
   * page (PRT-004).
   */
  onThePage: {
    /** Their own name, or a neutral heading when the circle has not recorded one. */
    heading: (name: string | null): string =>
      name === null || name.trim() === "" ? "The week ahead" : `${name.trim()}’s week`,
    period: (days: 7 | 14, firstDay: string, lastDay: string): string =>
      `${String(days)} days · ${firstDay} to ${lastDay}`,
    whoIsComing: "Who is coming",
    /**
     * Plain, and deliberately not a state name. "Please recheck" is the family's word
     * for a piece of work; this is a sentence that tells her not to count on it yet.
     */
    stillBeingChecked: "Still being checked.",
    checkWith: (name: string): string => `Please check with ${name} if plans change.`,
    /** No name given: still an answer, rather than an absence where an answer should be. */
    checkWithWhoeverBroughtIt:
      "Please check with the person who gave you this sheet if plans change.",
  },
} as const;

/**
 * Notification copy (NTF-006/-009/-014; bead hf-bs4).
 *
 * Its own module because it is the one catalogue with a non-UI consumer — a Convex
 * action renders it into an email or a text message — and because it is the only copy
 * here a stranger can read without the app being open. Re-exported rather than merely
 * written, because `toneGuard` sweeps THIS module's namespace: copy that is not
 * reachable from here is copy nothing checks (bead hf-1bu).
 */
export * from "./notifications.js";

/**
 * The saved §20.4 contact and its deliberately temporary printout override (bead hf-gxq).
 *
 * These are separate choices said plainly: settings changes the next sheet too; the
 * print control changes only the sheet in front of the organiser. Neither is framed
 * as urgent, and neither guesses a person from their role.
 */
export const SUPPORTED_PERSON_PRINT_CONTACT_MESSAGES = {
  settingsLabel: "Who they should check with",
  settingsHint:
    "Saved as the usual person to check with on their printed sheet. Leave it blank to use the unnamed fallback.",
  settingsAction: "Save who to check with",
  validation: "Use a name or short description of up to 120 characters.",
  overrideHint:
    "This starts with the saved choice. Change it here for this printout only; changes here are not saved.",
} as const;

/**
 * Closed-admission language (AUT-010/-018/-019; bead hf-e9-clerk-identity-xnf.2).
 *
 * The private-plan dead-end deliberately names neither a plan nor a person. It is the
 * same answer whether the route is unknown, the signed identifier is absent, or an
 * allowlist entry was removed, so the words cannot become an enumeration side channel.
 */
export const ADMISSION_ALLOWLIST_MESSAGES = {
  heading: "Who may join",
  listTitle: "Allowed email addresses",
  explanation:
    "Add an email address only after the person has agreed to join. They will still need an invitation.",
  emailLabel: "Email address",
  emailHint: "We store it encrypted and show only a masked version here.",
  addAction: "Allow this email",
  removeAction: "Remove",
  empty: "No email addresses have been added yet.",
  added: "The email address may now continue to an invitation.",
  removed: "The email address can no longer continue.",
  invalid: "Enter a complete email address.",
  unavailable: "That change could not be saved. Please try again when the connection is ready.",
  privatePlan: {
    title: "This plan is private",
    body: "Ask the organiser for an invitation.",
  },
} as const;

/**
 * Destination-bound invitations (AUT-018/-019; bead hf-e9-clerk-identity-xnf.3).
 *
 * An invitation is an offer made after agreement, not a demand. Expiry and refusal
 * copy say what can happen next without implying that the recipient caused a problem.
 */
export const INVITATION_LIFECYCLE_MESSAGES = {
  admin: {
    heading: "Invite someone",
    explanation:
      "Add someone after they have agreed to join. Their email is stored encrypted and the invitation can be used only once.",
    nameLabel: "Their name",
    emailLabel: "Their email address",
    action: "Send invitation",
    sending: "Sending invitation…",
    sent: "Invitation ready.",
    invalidName: "Enter the name they want shown in the plan.",
    invalidEmail: "Enter a complete email address.",
    alreadyInvited: "There is already a current invitation for that address.",
    unavailable:
      "That invitation could not be saved. Please try again when the connection is ready.",
    currentHeading: "Current invitations",
    empty: "No invitations have been sent yet.",
    resendAction: "Send a new link",
    revokeAction: "Withdraw invitation",
    state: {
      provisioning: "Being prepared",
      ready: "Ready to use",
      accepted: "Accepted",
      expired: "Expired",
      revoked: "Withdrawn",
      provisioning_failed: "Could not be prepared",
    },
  },
  notification: {
    subject: "Your private Here For invitation",
    emailBody: (link: string): string =>
      `You have been invited to a private family plan. Open Here For with your one-time invitation: ${link}`,
    smsBody: (link: string): string => `Open Here For with your private invitation: ${link}`,
  },
  landing: {
    heading: "Your invitation",
    explanation: "Continue with the email address this invitation was sent to.",
    action: "Open the plan",
    working: "Checking your invitation…",
    accepted: "You’re in. Opening the plan…",
    expiredTitle: "This invitation has expired",
    expiredBody: "Ask the organiser for a new invitation.",
    privateTitle: "This plan is private",
    privateBody: "Ask the organiser for an invitation.",
  },
} as const;

/**
 * Custom Clerk sign-in and shared-device session copy (AUT-003/-008/-024/-029;
 * bead hf-e9-clerk-identity-xnf.5).
 *
 * The landing half is deliberately data-free: it is rendered before Convex has
 * accepted an identity and therefore cannot name a circle, a person or a plan.
 * The seven-day sentence mirrors the Clerk maximum-lifetime decision; the app
 * never stores a parallel token or silently extends that provider-owned session.
 */
export const IDENTITY_SESSION_MESSAGES = {
  landing: {
    heading: "Private by design",
    body: "Sign in to continue. Nothing personal is shown before access is checked.",
    action: "Sign in",
  },
  loading: "Checking your session",
  signIn: {
    heading: "Sign in to Here For",
    introduction: "Use the email address or mobile number on your invitation.",
    channelLegend: "How should we send your code?",
    emailChannel: "Email",
    smsChannel: "Text message",
    emailLabel: "Email address",
    emailHint: "We’ll send a six-digit code. No password is needed.",
    phoneLabel: "Mobile number",
    phoneHint: "Include the country code, for example +44.",
    sendCode: "Send code",
    sendingCode: "Sending code…",
    codeHeading: "Enter your code",
    codeLabel: "Six-digit code",
    codeHint:
      "It can take a minute to arrive. You can paste the code, and it remains valid for ten minutes.",
    verifyCode: "Continue",
    verifyingCode: "Checking code…",
    changeDestination: "Use a different address or number",
    resend: "Send another code",
    resendIn: (seconds: number): string => `You can ask for another code in ${String(seconds)}s.`,
    resent: "Another code has been sent.",
    invalidCode: "That code wasn’t accepted. Check all six digits and try again.",
    rateLimited: "Please wait a moment before asking for another code.",
    unavailable: "Sign-in could not continue just now. Please try again.",
  },
  session: {
    heading: "Your session",
    lifetime:
      "This browser stays signed in for no longer than seven days. On a shared device, sign out when you finish.",
    signOut: "Sign out",
    switchAccount: "Use another account",
  },
} as const;

/**
 * Identity-reconciliation alerts (AUT-027/-028/-038; bead hf-e9-clerk-identity-xnf.6).
 *
 * These are administrative facts, not alarms. They say that access was paused for
 * review without naming an identifier, suggesting wrongdoing, or exposing provider
 * detail a family member cannot act on.
 */
export const IDENTITY_RECONCILIATION_MESSAGES = {
  heading: "Account access checks",
  repairedTitle: "Some account access was paused",
  repairedBody: (count: number): string =>
    `${String(count)} ${count === 1 ? "person needs" : "people need"} an organiser to check their sign-in details before access can continue.`,
  failedTitle: "The latest account check could not finish",
  failedBody: "No access was added. Please try the account check again when the service is ready.",
  checkedSummary: (checked: number, repaired: number): string =>
    `${String(checked)} checked · ${String(repaired)} paused for review`,
} as const;

/**
 * First-run orientation and replayable help (ONB-001–ONB-012; Phase 8).
 *
 * Each archetype gets one short route into the part of the plan it can actually use.
 * The shared sentences are the mental model, not a tour of the navigation: family
 * conversation stays in WhatsApp, this plan records agreed truth, and availability
 * remains a possibility until somebody explicitly agrees.
 */
export const ONBOARDING_MESSAGES = {
  eyebrow: "A quick introduction",
  organiser: {
    heading: "Set up your first week",
    introduction:
      "Start with one thing that is happening. You can add the help around it afterwards.",
    primaryAction: "Add the first plan item",
  },
  helper: {
    heading: (organiserName: string | null): string =>
      organiserName === null
        ? "The organiser shared this plan with you"
        : `${organiserName} shared this plan with you`,
    introduction:
      "See what the circle has agreed, then add times when you could be asked about helping.",
    primaryAction: "Add times I could help",
  },
  viewOnly: {
    heading: "A calm view of the family plan",
    introduction:
      "You can read what is happening and what help has been agreed. This view does not ask you to change anything.",
    primaryAction: "See the plan",
  },
  mentalModel: {
    conversation: "WhatsApp is where the family talks.",
    sourceOfTruth: "Here For is where you check what has actually been agreed.",
    availability:
      "Availability means you could be asked. It does not book you in or promise your help.",
  },
  skip: "Skip this introduction",
  privacyLink: "How Here For keeps this plan private",
  practice: {
    action: "Try a quick practice example",
    heading: "Practice only — nothing will be saved",
    need: "A lift to a made-up appointment",
    state: "Needs someone",
    explanation: "Marking a time as available would still leave this needing someone.",
    agreeAction: "Practise saying yes",
    covered: "Covered",
    result:
      "In this practice, saying yes covers the need. Nothing was added to the family plan and nobody was notified.",
    finish: "Finish practice",
  },
  help: {
    heading: "Help with the plan",
    introduction: "The short version of how Here For works.",
    withdrawalTitle: "If your plans change",
    withdrawalBody:
      "You can step back from something you agreed to. The plan keeps the history and shows that help is needed again; you never have to give a reason.",
    sharingTitle: "Sharing stays inside the circle",
    sharingBody:
      "Plan links still require sign-in and current access. Share only the practical detail somebody needs.",
    emergencyTitle: "For urgent help",
    emergencyBody:
      "Here For is not monitored as an emergency service. Contact the appropriate person or emergency service directly.",
  },
} as const;

/** Plain-language, code-backed privacy promises for the onboarding trust surface. */
export const PRIVACY_PAGE_MESSAGES = {
  heading: "Privacy in plain language",
  introduction:
    "Here For is a private coordination plan for a small circle. It is not a public profile or a social network.",
  stored: {
    title: "What the plan stores",
    body: "The plan stores the circle’s people, events, support needs, availability, questions, agreements and practical updates. Contact destinations used for invitations and notifications are encrypted before storage and shown only in masked form where possible.",
  },
  location: {
    title: "Where the data lives",
    current: "The service is still in development, so only synthetic test data is allowed today.",
    production:
      "Before any real family data is added, the production Convex deployment must be verified in EU West (Ireland). That production deployment has not been provisioned yet.",
  },
  visibility: {
    title: "Who can see what",
    body: "A signed-in person sees the plan only while they have a current circle membership. Each action and sensitive view is checked against that membership; contact details and restricted notes have narrower access of their own.",
  },
  choice: {
    title: "Helping is always your choice",
    body: "Adding availability only says you could be asked. A request does not count as help agreed. The plan changes to Covered only after enough people explicitly say yes.",
  },
  export: {
    title: "Get a copy of the circle’s data",
    body: "People with export access can download a portable copy. The download is created only after the server confirms that access.",
    action: "Download a portable copy",
    working: "Preparing your portable copy…",
    ready: "Your portable copy is ready.",
    unavailable: "Your membership does not include data export. Ask an organiser for a copy.",
    failed: "The copy could not be prepared. Nothing was downloaded. Please try again.",
  },
  backToPlan: "Back to the plan",
  settingsLink: "Read the privacy page",
} as const;
